import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Plus, Sparkles, Upload, Edit3, Trash2, Eye, Layers, Palette, ArrowLeft, Loader2, Phone, Image as ImageIcon, Search, X, Users, Camera, ScanSearch, ChevronRight } from 'lucide-react';
import ProductAddForm from './ProductAddForm';
import FabricCatalogueView from './FabricCatalogueView';
import { useBackButton } from '../hooks/useBackButton';
import { getProducts, semanticSearchProducts, visualSearchProducts, getFabricTypes, getWeaveKnitValues, getOpacityValues, getStretchValues, getColorValues, createProduct, updateProduct, deleteProduct, updateProductVisibility } from '../api/product.api';
import { analyzeFabric } from '../api/fabricAnalysis.api';
import { getFollowersCount } from '../api';
import { NotificationProvider, useNotifications } from './notifications/NotificationContext';
import {
  getFabricCollections,
  createFabricCollection,
  deleteFabricCollection,
  updateFabricCollection,
  updatePromotedCollectionVisibility,
} from '../api/fabricCollection.api';
import { getPromotedVendors, getPromotedVendorProducts, getPromotedVendorCollections } from '../api/promotionRequest.api';
import { uploadImageToS3, compressImage, isHeicFile, normalizeImageFile } from '../utils/s3Upload';
import { toThumbnailUrl } from '../utils/imageThumbnailUrl';

const INITIAL_FABRIC_COLLECTION_FORM = {
  name: '',
  imageFile: null,
  imagePreview: null,
  properties: [{ key: '', value: '' }],
  selectedFabrics: [],
  removeImage: false,
  tags: []
};

/** Aligns API `scores[i]` with `products[i]` so lookup does not depend on _id string/ObjectId quirks. */
function mergeVisualSearchScores(products, visualScores, isVisualSearch) {
  const list = Array.isArray(products) ? products : [];
  if (!isVisualSearch || !Array.isArray(visualScores)) {
    return list.map(({ visualMatchScore: _drop, ...rest }) => rest);
  }
  return list.map((p, i) => {
    const { visualMatchScore: _prev, ...base } = p;
    const raw = visualScores[i];
    if (raw == null) return base;
    const n = typeof raw === 'number' ? raw : Number(raw);
    if (!Number.isFinite(n)) return base;
    return { ...base, visualMatchScore: n };
  });
}

const LITE_CARD_DOUBLE_TAP_MS = 320;

// Lazy Image Component with IntersectionObserver
const isLowResThumbnailUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  return /\/thumbnails\//i.test(url) || /-thumbnail\.(jpe?g|png|webp)(\?|#|$)/i.test(url);
};

/** Prefer full-resolution fabric images; skip generated -thumbnail assets when a better URL exists. */
const getFabricItemImageUrl = (fabric) => {
  if (!fabric || typeof fabric !== 'object') return null;
  const candidates = [
    fabric.images?.original?.s3Url,
    fabric.images?.original?.url,
    fabric.images?.original?.imgbbUrl,
    fabric.image,
  ]
    .filter(Boolean)
    .map((u) => String(u).trim())
    .filter(Boolean);

  if (candidates.length === 0) return null;
  const fullRes = candidates.find((u) => !isLowResThumbnailUrl(u));
  return fullRes || candidates[0];
};

const LazyImage = ({ src, alt, className, onError, fallbackSrc, ...props }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [useFallback, setUseFallback] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: '200px',
        threshold: 0.01
      }
    );

    observer.observe(img);

    return () => {
      observer.disconnect();
    };
  }, []);

  // Reset load state when the source changes (e.g. fabric list refresh).
  useEffect(() => {
    setIsLoaded(false);
    setHasError(false);
    setUseFallback(false);
  }, [src, fallbackSrc]);

  const handleLoad = () => {
    setIsLoaded(true);
  };

  const handleError = (e) => {
    if (!useFallback && fallbackSrc && fallbackSrc !== src) {
      setUseFallback(true);
      setIsLoaded(false);
      return;
    }
    setHasError(true);
    setIsLoaded(true);
    if (onError) {
      onError(e);
    }
  };

  const activeSrc = useFallback ? fallbackSrc : src;

  return (
    <div ref={imgRef} className="relative w-full h-full pointer-events-none">
      {/* Placeholder/Loading State */}
      {!isLoaded && (
        <div className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center z-0">
          <Loader2 size={24} className="text-gray-400 animate-spin" />
        </div>
      )}
      
      {/* Actual Image */}
      {isInView && activeSrc && (
        <img
          src={activeSrc}
          alt={alt}
          className={`${className} transition-opacity duration-300 relative z-10 ${
            isLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          onLoad={handleLoad}
          onError={handleError}
          loading="lazy"
          decoding="async"
          {...props}
        />
      )}
      
      {/* Error State */}
      {hasError && isInView && (
        <div className="absolute inset-0 bg-gray-100 flex items-center justify-center z-20">
          <div className="text-center p-2">
            <Layers size={24} className="text-gray-400 mx-auto mb-1" />
            <p className="text-xs text-gray-500">Image unavailable</p>
          </div>
        </div>
      )}
    </div>
  );
};

const FabricCard = React.memo(function FabricCard({
  fabric,
  isAdmin,
  isVisibilityLoading,
  isAnalysisLoading,
  onViewCatalogue,
  onGenerate,
  onEdit,
  onDelete,
  onToggleVisibility,
  onRunAnalysis,
  promotedFrom,
  liteMode = false,
}) {
  const visualMatchScore = fabric.visualMatchScore;
  const { imageUrl } = useMemo(() => {
    const url =
      getFabricItemImageUrl(fabric) ||
      'https://via.placeholder.com/200x200?text=No+Image';
    return { imageUrl: url };
  }, [fabric]);

  const variantCount = fabric.colorVariants ? fabric.colorVariants.length : 0;
  const userPhone = useMemo(() => {
    const phone =
      fabric?.user?.phone ||
      fabric?.user?.phoneNumber ||
      fabric?.phone ||
      fabric?.phoneNumber ||
      fabric?.contactNumber;

    if (!phone) return null;

    if (typeof phone === 'string') {
      const trimmed = phone.trim();
      return trimmed.length ? trimmed : null;
    }

    return String(phone);
  }, [fabric]);
  const userPublicLink = useMemo(() => {
    if (!userPhone) return null;
    const normalized = userPhone.replace(/\D/g, '');
    if (!normalized) return null;
    return `https://fashdeck.com/${normalized}`;
  }, [userPhone]);

  const isPromotedCatalog = Boolean(promotedFrom);
  const lastTapRef = useRef({ time: 0, key: null });

  const handleCardSurfaceActivate = () => {
    if (!liteMode) {
      onViewCatalogue(fabric);
      return;
    }
    const key = String(fabric?._id || fabric?.id || '');
    const now = performance.now();
    const prev = lastTapRef.current;
    if (prev.key === key && now - prev.time <= LITE_CARD_DOUBLE_TAP_MS) {
      lastTapRef.current = { time: 0, key: null };
      onViewCatalogue(fabric);
      return;
    }
    lastTapRef.current = { time: now, key };
  };

  return (
    <div className="group bg-white rounded-lg sm:rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all duration-300 overflow-hidden">
      <div
        className="relative aspect-square bg-gray-100 cursor-pointer overflow-hidden"
        onClick={handleCardSurfaceActivate}
        title={liteMode ? 'Double tap to view fabric detail' : 'View fabric catalogue'}
      >
        <LazyImage
          src={imageUrl}
          alt={fabric.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
        />
        {variantCount > 0 && (
          <div className="absolute top-1 right-1 sm:top-2 sm:right-2 bg-blue-500 text-white text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full font-medium">
            +{variantCount}
          </div>
        )}
        {fabric.fabricTexture && (
          <div className="absolute top-1 left-1 sm:top-2 sm:left-2 bg-green-500 text-white text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full font-medium">
            {fabric.fabricTexture.name}
          </div>
        )}
        {isAdmin && userPhone && userPublicLink && (
          <a
            href={userPublicLink}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="absolute bottom-1 left-1 sm:bottom-2 sm:left-2 bg-purple-500 text-white text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full font-medium flex items-center gap-1 hover:bg-purple-600 transition-colors"
          >
            <Phone size={10} />
            <span>{userPhone}</span>
          </a>
        )}
        <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 opacity-0 group-hover:opacity-100 transition-all duration-300">
          <div className="bg-white/90 backdrop-blur-sm px-3 py-2 rounded-lg shadow-lg transform scale-90 group-hover:scale-100 transition-transform">
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
              <Eye size={16} />
              <span>View Catalogue</span>
            </div>
          </div>
        </div>
      </div>

      <div className="p-3 sm:p-4">
        <h3
          className="text-sm sm:text-base font-semibold text-gray-900 line-clamp-2 leading-snug mb-2 sm:mb-3"
          title={fabric.name || 'Untitled fabric'}
        >
          {fabric.name?.trim() || 'Untitled fabric'}
        </h3>
        {Number.isFinite(visualMatchScore) && (
          <div
            className="mb-2 sm:mb-3 flex items-center gap-1.5 rounded-lg bg-cyan-50 border border-cyan-100 px-2 py-1.5 text-xs font-semibold text-cyan-900"
            title="Visual similarity (cosine)"
          >
            <ScanSearch size={14} className="text-cyan-600 shrink-0" aria-hidden />
            <span className="tabular-nums">{(visualMatchScore * 100).toFixed(1)}% visual match</span>
          </div>
        )}
        {isPromotedCatalog && promotedFrom?.label && (
          <div className="mb-2 text-[11px] sm:text-xs font-semibold text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5">
            Promoted vendor: {promotedFrom.label}
          </div>
        )}
        {isAdmin && !liteMode && userPhone && userPublicLink && (
          <a
            href={userPublicLink}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="flex items-center gap-1 text-xs sm:text-sm text-purple-600 font-medium mb-2 sm:mb-3 hover:text-purple-700 transition-colors"
          >
            <Phone size={12} className="sm:w-3 sm:h-3" />
            <span>{userPhone}</span>
          </a>
        )}

        {!liteMode && (
          !isPromotedCatalog ? (
          <div
            className="flex items-center justify-between mb-2 sm:mb-3 text-xs"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col">
              <span className="text-gray-500 font-medium">Push to marketplace</span>
              {fabric.isPublic && <span className="text-[10px] text-gray-400">Visible in marketplace</span>}
            </div>
            <label className={`relative inline-flex items-center cursor-pointer ${isVisibilityLoading ? 'opacity-60 cursor-not-allowed' : ''}`}>
              <input
              
              type="checkbox"
                className="sr-only peer"
                checked={!!fabric.isPublic}
                onChange={(e) => onToggleVisibility(fabric, e.target.checked)}
                disabled={isVisibilityLoading}
              />
              <div className="w-10 h-5 bg-gray-300 rounded-full transition-colors duration-200 peer-checked:bg-green-500"></div>
              <span className="absolute left-0.5 top-0.5 h-4 w-4 bg-white rounded-full shadow transition-transform duration-200 peer-checked:translate-x-5"></span>
            </label>
          </div>
          ) : (
            <p className="text-[11px] sm:text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-2 py-1.5 mb-2 sm:mb-3">
              Public fabric from an approved vendor — view or generate only.
            </p>
          )
        )}

        <div className="flex flex-col gap-2">
          <div className="flex gap-1.5 sm:gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onGenerate(fabric);
              }}
              className="flex-1 bg-blue-600 text-white px-2 sm:px-3 py-1.5 sm:py-2 rounded-md sm:rounded-lg text-xs sm:text-sm font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-1"
              title="Generate kit with this fabric"
            >
              <Sparkles size={12} className="sm:w-3.5 sm:h-3.5" />
              <span className="hidden sm:inline">Generate</span>
              <span className="sm:hidden">Generate</span>
            </button>
            {!liteMode && !isPromotedCatalog && (
            <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit(fabric);
              }}
              className="px-2 sm:px-3 py-1.5 sm:py-2 bg-gray-100 text-gray-600 rounded-md sm:rounded-lg hover:bg-gray-200 transition-colors"
              title="Edit fabric"
            >
              <Edit3 size={12} className="sm:w-3.5 sm:h-3.5" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(fabric._id);
              }}
              className="px-2 sm:px-3 py-1.5 sm:py-2 bg-red-50 text-red-600 rounded-md sm:rounded-lg hover:bg-red-100 transition-colors"
              title="Delete fabric"
            >
              <Trash2 size={12} className="sm:w-3.5 sm:h-3.5" />
            </button>
            </>
            )}
          </div>
          {isAdmin && !liteMode && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onRunAnalysis(fabric);
              }}
              disabled={isAnalysisLoading}
              className={`w-full px-2 sm:px-3 py-1.5 sm:py-2 rounded-md sm:rounded-lg text-xs sm:text-sm font-medium transition-colors flex items-center justify-center gap-1 ${
                isAnalysisLoading
                  ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                  : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
              }`}
              title="Run AI fabric analysis"
            >
              {isAnalysisLoading ? <Loader2 size={12} className="sm:w-3.5 sm:h-3.5 animate-spin" /> : <Sparkles size={12} className="sm:w-3.5 sm:h-3.5" />}
              <span>{isAnalysisLoading ? 'Analyzing...' : 'Run Analysis'}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.fabric === nextProps.fabric &&
    prevProps.isAdmin === nextProps.isAdmin &&
    prevProps.isVisibilityLoading === nextProps.isVisibilityLoading &&
    prevProps.isAnalysisLoading === nextProps.isAnalysisLoading &&
    prevProps.promotedFrom === nextProps.promotedFrom &&
    prevProps.onViewCatalogue === nextProps.onViewCatalogue &&
    prevProps.onGenerate === nextProps.onGenerate &&
    prevProps.onEdit === nextProps.onEdit &&
    prevProps.onDelete === nextProps.onDelete &&
    prevProps.onToggleVisibility === nextProps.onToggleVisibility &&
    prevProps.onRunAnalysis === nextProps.onRunAnalysis &&
    prevProps.liteMode === nextProps.liteMode
  );
});


const LITE_ROW_ITEM_BASE = 268;
const LITE_ROW_ITEM_GAP = 12;
// Center stays at layout size (no CSS upscale — that softens images on retina).
const LITE_ROW_CENTER_SCALE = 1;
const LITE_ROW_EDGE_SCALE = 0.62;

/** Double-tap/click helper for lite-mode cards (single tap is reserved for scroll gestures). */
function useDoubleTapAction(timeoutMs = LITE_CARD_DOUBLE_TAP_MS) {
  const lastTapRef = useRef({ time: 0, key: null });

  return useCallback(
    (key, action) => {
      const now = performance.now();
      const prev = lastTapRef.current;
      if (prev.key === key && now - prev.time <= timeoutMs) {
        lastTapRef.current = { time: 0, key: null };
        action?.();
        return;
      }
      lastTapRef.current = { time: now, key };
    },
    [timeoutMs]
  );
}

/** Lite-mode collections: center-focus horizontal carousel (big center, smaller sides). */
const FabricCollectionLiteRow = React.memo(function FabricCollectionLiteRow({
  collection,
  childCollections = [],
  fabrics = [],
  onOpenCollection,
  onOpenFabric,
}) {
  const viewportRef = useRef(null);
  const trackRef = useRef(null);
  const leftSpacerRef = useRef(null);
  const rightSpacerRef = useRef(null);
  const itemRefs = useRef([]);
  const offsetRef = useRef(0);
  const animRafRef = useRef(0);
  const focusRafRef = useRef(0);
  const itemCentersRef = useRef([]);
  const promotedFrom = collection.promotedFrom;
  const handleDoubleTap = useDoubleTapAction();

  const items = useMemo(
    () => [
      ...childCollections.map((child) => ({ kind: 'child', id: `child-${child._id}`, data: child })),
      ...fabrics.map((fabric) => ({ kind: 'fabric', id: `fabric-${fabric._id}`, data: fabric })),
    ],
    [childCollections, fabrics]
  );

  const itemCount = items.length;

  const cancelAnimation = useCallback(() => {
    if (animRafRef.current) {
      cancelAnimationFrame(animRafRef.current);
      animRafRef.current = 0;
    }
  }, []);

  const syncEdgeSpacers = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport) return 16;
    // Exact side inset so first/last card centers can sit in the middle.
    const pad = Math.max(16, viewport.clientWidth / 2 - LITE_ROW_ITEM_BASE / 2);
    if (leftSpacerRef.current) leftSpacerRef.current.style.width = `${pad}px`;
    if (rightSpacerRef.current) rightSpacerRef.current.style.width = `${pad}px`;
    return pad;
  }, []);

  const cacheItemCenters = useCallback(() => {
    itemCentersRef.current = itemRefs.current.map((el) =>
      el ? el.offsetLeft + el.offsetWidth / 2 : 0
    );
  }, []);

  const getMinOffset = useCallback(() => {
    const viewport = viewportRef.current;
    const centers = itemCentersRef.current;
    if (!viewport || !centers.length) return 0;
    return Math.max(0, centers[0] - viewport.clientWidth / 2);
  }, []);

  const getMaxOffset = useCallback(() => {
    const viewport = viewportRef.current;
    const centers = itemCentersRef.current;
    if (!viewport || !centers.length) return 0;
    // Allow scrolling far enough that the LAST card can sit dead-center.
    return Math.max(0, centers[centers.length - 1] - viewport.clientWidth / 2);
  }, []);

  const clampOffset = useCallback(
    (value) => Math.max(getMinOffset(), Math.min(getMaxOffset(), value)),
    [getMaxOffset, getMinOffset]
  );

  const applyOffset = useCallback((value, { syncFocus = true } = {}) => {
    const track = trackRef.current;
    if (!track) return;
    offsetRef.current = value;
    track.style.transform = `translate3d(${-value}px, 0, 0)`;
    if (!syncFocus) return;
    if (focusRafRef.current) return;
    focusRafRef.current = requestAnimationFrame(() => {
      focusRafRef.current = 0;
      const viewport = viewportRef.current;
      if (!viewport) return;
      const centerX = offsetRef.current + viewport.clientWidth / 2;
      const maxDist = Math.max(viewport.clientWidth * 0.45, LITE_ROW_ITEM_BASE);
      const centers = itemCentersRef.current;
      itemRefs.current.forEach((el, index) => {
        if (!el) return;
        const itemCenter = centers[index] || el.offsetLeft + el.offsetWidth / 2;
        const t = Math.min(1, Math.abs(centerX - itemCenter) / maxDist);
        const ease = t * t * (3 - 2 * t);
        const scale =
          LITE_ROW_CENTER_SCALE - ease * (LITE_ROW_CENTER_SCALE - LITE_ROW_EDGE_SCALE);
        const opacity = 1 - ease * 0.42;
        const y = ease * 14;
        el.style.transform = `translate3d(0, ${y}px, 0) scale(${scale})`;
        el.style.opacity = String(opacity);
        el.style.zIndex = String(Math.round((1 - ease) * 20));
      });
    });
  }, []);

  const indexNearestTo = useCallback((trackX) => {
    const centers = itemCentersRef.current;
    if (!centers.length) return 0;
    let best = 0;
    let bestDist = Math.abs(centers[0] - trackX);
    for (let i = 1; i < centers.length; i += 1) {
      const dist = Math.abs(centers[i] - trackX);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    }
    return best;
  }, []);

  const offsetForIndex = useCallback(
    (index) => {
      const viewport = viewportRef.current;
      const centers = itemCentersRef.current;
      if (!viewport || !centers.length) return 0;
      const i = Math.max(0, Math.min(centers.length - 1, index));
      return clampOffset(centers[i] - viewport.clientWidth / 2);
    },
    [clampOffset]
  );

  /** Directional settle: advance with the swipe, never spring backward against it. */
  const getSnapTargetOffset = useCallback(
    (velocity, startOffset) => {
      const viewport = viewportRef.current;
      const centers = itemCentersRef.current;
      if (!viewport || !centers.length) return offsetRef.current;

      const vw = viewport.clientWidth;
      const startIdx = indexNearestTo(startOffset + vw / 2);
      const currentIdx = indexNearestTo(offsetRef.current + vw / 2);
      const dragDelta = offsetRef.current - startOffset;
      const stride = LITE_ROW_ITEM_BASE + LITE_ROW_ITEM_GAP;
      const goingNext = velocity > 0.05 || dragDelta > Math.max(10, stride * 0.08);
      const goingPrev = velocity < -0.05 || dragDelta < -Math.max(10, stride * 0.08);

      let targetIdx = currentIdx;
      if (goingNext) {
        // Always at least one step forward from where the gesture started — no pull-back.
        targetIdx = Math.max(currentIdx, startIdx + 1);
        if (velocity > 0.5) targetIdx += 1;
        if (velocity > 0.9) targetIdx += 1;
      } else if (goingPrev) {
        targetIdx = Math.min(currentIdx, startIdx - 1);
        if (velocity < -0.5) targetIdx -= 1;
        if (velocity < -0.9) targetIdx -= 1;
      } else {
        targetIdx = startIdx;
      }

      targetIdx = Math.max(0, Math.min(centers.length - 1, targetIdx));
      return offsetForIndex(targetIdx);
    },
    [indexNearestTo, offsetForIndex]
  );

  const animateTo = useCallback(
    (target, duration = 150) => {
      cancelAnimation();
      const start = offsetRef.current;
      const delta = target - start;
      if (Math.abs(delta) < 0.5) {
        applyOffset(target);
        return;
      }
      const startTime = performance.now();
      const step = (now) => {
        const t = Math.min(1, (now - startTime) / duration);
        // easeOutQuad — quick settle, no soft bounce feel
        const eased = 1 - (1 - t) * (1 - t);
        applyOffset(start + delta * eased);
        if (t < 1) {
          animRafRef.current = requestAnimationFrame(step);
        } else {
          animRafRef.current = 0;
          applyOffset(target);
        }
      };
      animRafRef.current = requestAnimationFrame(step);
    },
    [applyOffset, cancelAnimation]
  );

  useEffect(() => {
    itemRefs.current = itemRefs.current.slice(0, items.length);
  }, [items]);

  useEffect(() => {
    const viewport = viewportRef.current;
    const track = trackRef.current;
    if (!viewport || !track || itemCount === 0) return undefined;

    const layout = () => {
      syncEdgeSpacers();
      cacheItemCenters();
      applyOffset(clampOffset(offsetRef.current));
    };

    const recenterFirst = () => {
      syncEdgeSpacers();
      cacheItemCenters();
      applyOffset(offsetForIndex(0));
    };

    requestAnimationFrame(recenterFirst);

    const ro =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => {
            layout();
          })
        : null;
    ro?.observe(viewport);
    ro?.observe(track);

    // Single-finger horizontal carousel; vertical gestures are left to native page scroll.
    let activeTouchId = null;
    let mouseActive = false;
    let startX = 0;
    let startY = 0;
    let startOffset = 0;
    let axis = null; // 'x' | 'y' | null
    let moved = false;
    let lastX = 0;
    let lastT = 0;
    let velocity = 0;

    const beginDrag = (clientX, clientY) => {
      cancelAnimation();
      startX = clientX;
      startY = clientY;
      lastX = clientX;
      lastT = performance.now();
      startOffset = offsetRef.current;
      axis = null;
      moved = false;
      velocity = 0;
      syncEdgeSpacers();
      cacheItemCenters();
    };

    const moveDragX = (clientX, event) => {
      const dx = clientX - startX;
      moved = true;
      if (event?.cancelable) event.preventDefault();
      const now = performance.now();
      const dt = Math.max(1, now - lastT);
      const sample = -((clientX - lastX) / dt);
      velocity = velocity * 0.55 + sample * 0.45;
      lastX = clientX;
      lastT = now;
      applyOffset(clampOffset(startOffset - dx));
    };

    const finishDrag = (clientX) => {
      const wasHorizontal = moved && axis === 'x';
      if (wasHorizontal) {
        const suppressClick = (clickEvent) => {
          clickEvent.preventDefault();
          clickEvent.stopPropagation();
        };
        viewport.addEventListener('click', suppressClick, { capture: true, once: true });
        window.setTimeout(() => {
          viewport.removeEventListener('click', suppressClick, { capture: true });
        }, 400);

        const now = performance.now();
        const dt = Math.max(1, now - lastT);
        if (dt < 64 && typeof clientX === 'number') {
          const sample = -((clientX - lastX) / dt);
          velocity = velocity * 0.3 + sample * 0.7;
        }

        cacheItemCenters();
        animateTo(getSnapTargetOffset(velocity, startOffset), 120);
      }
      activeTouchId = null;
      mouseActive = false;
      axis = null;
      moved = false;
    };

    const onTouchStart = (e) => {
      if (e.touches.length !== 1) {
        activeTouchId = null;
        axis = null;
        return;
      }
      const t = e.touches[0];
      activeTouchId = t.identifier;
      beginDrag(t.clientX, t.clientY);
    };

    const onTouchMove = (e) => {
      if (e.touches.length !== 1 || activeTouchId === null) return;
      let t = null;
      for (let i = 0; i < e.touches.length; i += 1) {
        if (e.touches[i].identifier === activeTouchId) {
          t = e.touches[i];
          break;
        }
      }
      if (!t) t = e.touches[0];
      if (!t) return;

      const dx = t.clientX - startX;
      const dy = t.clientY - startY;
      if (axis === null) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        // Prefer vertical when ambiguous so page scroll stays smooth.
        axis = Math.abs(dy) >= Math.abs(dx) ? 'y' : 'x';
        if (axis === 'y') {
          // Release so native vertical scrolling (with momentum) can take over.
          activeTouchId = null;
          return;
        }
      }
      if (axis === 'y') return;
      moveDragX(t.clientX, e);
    };

    const onTouchEnd = (e) => {
      if (activeTouchId === null) return;
      for (let i = 0; i < e.changedTouches.length; i += 1) {
        if (e.changedTouches[i].identifier === activeTouchId) {
          finishDrag(e.changedTouches[i].clientX);
          return;
        }
      }
      if (e.touches.length === 0) finishDrag(lastX);
    };

    const onPointerDown = (e) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      if (mouseActive) return;
      mouseActive = true;
      beginDrag(e.clientX, e.clientY);
      try {
        viewport.setPointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
    };

    const onPointerMove = (e) => {
      if (!mouseActive || e.pointerType !== 'mouse') return;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      if (axis === null) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        if (axis === 'y') {
          mouseActive = false;
          return;
        }
      }
      if (axis !== 'x') return;
      moveDragX(e.clientX, e);
    };

    const endPointer = (e) => {
      if (!mouseActive || e.pointerType !== 'mouse') return;
      finishDrag(e.clientX);
      try {
        if (viewport.hasPointerCapture?.(e.pointerId)) {
          viewport.releasePointerCapture(e.pointerId);
        }
      } catch {
        /* ignore */
      }
    };

    viewport.addEventListener('touchstart', onTouchStart, { passive: true, capture: true });
    viewport.addEventListener('touchmove', onTouchMove, { passive: false, capture: true });
    viewport.addEventListener('touchend', onTouchEnd, { passive: true, capture: true });
    viewport.addEventListener('touchcancel', onTouchEnd, { passive: true, capture: true });
    viewport.addEventListener('pointerdown', onPointerDown, { passive: true, capture: true });
    viewport.addEventListener('pointermove', onPointerMove, { passive: false, capture: true });
    viewport.addEventListener('pointerup', endPointer, { passive: true, capture: true });
    viewport.addEventListener('pointercancel', endPointer, { passive: true, capture: true });

    return () => {
      cancelAnimation();
      if (focusRafRef.current) cancelAnimationFrame(focusRafRef.current);
      viewport.removeEventListener('touchstart', onTouchStart, true);
      viewport.removeEventListener('touchmove', onTouchMove, true);
      viewport.removeEventListener('touchend', onTouchEnd, true);
      viewport.removeEventListener('touchcancel', onTouchEnd, true);
      viewport.removeEventListener('pointerdown', onPointerDown, true);
      viewport.removeEventListener('pointermove', onPointerMove, true);
      viewport.removeEventListener('pointerup', endPointer, true);
      viewport.removeEventListener('pointercancel', endPointer, true);
      ro?.disconnect();
    };
  }, [
    itemCount,
    items.length,
    animateTo,
    applyOffset,
    cacheItemCenters,
    cancelAnimation,
    clampOffset,
    getSnapTargetOffset,
    offsetForIndex,
    syncEdgeSpacers,
  ]);

  return (
    <section
      className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-x-clip"
      style={{ contentVisibility: 'auto', containIntrinsicSize: 'auto 420px' }}
    >
      <div className="flex items-center justify-between gap-3 px-4 pt-4 pb-1">
        <button
          type="button"
          onClick={() => onOpenCollection?.(collection._id)}
          className="min-w-0 text-left group"
        >
          <div className="flex items-center gap-2 min-w-0">
            <h3 className="font-semibold text-gray-900 text-base truncate group-hover:text-purple-700 transition-colors">
              {collection.name}
            </h3>
            <ChevronRight
              size={16}
              className="shrink-0 text-gray-400 group-hover:text-purple-500 transition-colors"
              aria-hidden
            />
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            {itemCount} item{itemCount === 1 ? '' : 's'}
            {promotedFrom?.label ? ` · ${promotedFrom.label}` : ''}
          </p>
        </button>
        <button
          type="button"
          onClick={() => onOpenCollection?.(collection._id)}
          className="shrink-0 text-xs font-semibold text-purple-600 hover:text-purple-700 px-2 py-1 rounded-lg hover:bg-purple-50 transition-colors"
        >
          View all
        </button>
      </div>

      {itemCount === 0 ? (
        <div className="px-4 pb-4 pt-2">
          <button
            type="button"
            onClick={() => onOpenCollection?.(collection._id)}
            className="w-full rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-8 text-center text-sm text-gray-500 hover:border-purple-300 hover:bg-purple-50/40 transition-colors"
          >
            No items yet — open collection
          </button>
        </div>
      ) : (
        <div
          ref={viewportRef}
          className="relative overflow-hidden pb-3 pt-2"
          style={{
            minHeight: 360,
            // Native vertical page scroll; horizontal is claimed only after axis lock.
            touchAction: 'pan-y',
            WebkitOverflowScrolling: 'touch',
          }}
          role="list"
          aria-label={`${collection.name} items`}
        >
          <div
            ref={trackRef}
            className="flex items-center will-change-transform"
            style={{
              gap: LITE_ROW_ITEM_GAP,
              transform: 'translate3d(0, 0, 0)',
              backfaceVisibility: 'hidden',
            }}
          >
            <div ref={leftSpacerRef} className="shrink-0" style={{ width: 16 }} aria-hidden />
            {items.map((item, index) => {
              if (item.kind === 'child') {
                const child = item.data;
                const childImage =
                  child.image?.url || getFabricItemImageUrl(child.fabrics?.[0]) || null;
                const fabricCount = Array.isArray(child.fabrics) ? child.fabrics.length : 0;
                return (
                  <button
                    key={item.id}
                    type="button"
                    role="listitem"
                    ref={(el) => {
                      itemRefs.current[index] = el;
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      handleDoubleTap(item.id, () => onOpenCollection?.(child._id));
                    }}
                    title="Double tap to open collection"
                    className="shrink-0 text-left group focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400 rounded-2xl origin-center"
                    style={{
                      width: LITE_ROW_ITEM_BASE,
                      transform: `scale(${LITE_ROW_EDGE_SCALE})`,
                      opacity: 0.75,
                      touchAction: 'pan-y',
                      willChange: 'transform, opacity',
                    }}
                  >
                    <div className="relative aspect-square rounded-2xl overflow-hidden bg-gradient-to-br from-purple-50 via-pink-50 to-indigo-50 border border-white shadow-md group-hover:shadow-lg transition-shadow">
                      {childImage ? (
                        <LazyImage
                          src={childImage}
                          alt={child.name}
                          className="w-full h-full object-cover pointer-events-none"
                          sizes="200px"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center px-2 pointer-events-none">
                          <Layers size={28} className="text-purple-400" />
                        </div>
                      )}
                      <div className="absolute top-1.5 left-1.5 bg-purple-600 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-full shadow pointer-events-none">
                        Collection
                      </div>
                      {fabricCount > 0 && (
                        <div className="absolute top-1.5 right-1.5 bg-blue-500 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-full shadow pointer-events-none">
                          {fabricCount}
                        </div>
                      )}
                    </div>
                    <p className="mt-2 text-[11px] font-semibold text-gray-800 line-clamp-2 leading-snug text-center px-0.5 pointer-events-none">
                      {child.name}
                    </p>
                  </button>
                );
              }

              const fabric = item.data;
              const imageUrl = getFabricItemImageUrl(fabric);
              return (
                <button
                  key={item.id}
                  type="button"
                  role="listitem"
                  ref={(el) => {
                    itemRefs.current[index] = el;
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    handleDoubleTap(item.id, () => onOpenFabric?.(fabric));
                  }}
                  title="Double tap to view fabric detail"
                  className="shrink-0 text-left group focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded-2xl origin-center"
                  style={{
                    width: LITE_ROW_ITEM_BASE,
                    transform: `scale(${LITE_ROW_EDGE_SCALE})`,
                    opacity: 0.75,
                    touchAction: 'pan-y',
                    willChange: 'transform, opacity',
                  }}
                >
                  <div className="relative aspect-square rounded-2xl overflow-hidden bg-gray-100 border border-white shadow-md group-hover:shadow-lg transition-shadow">
                    {imageUrl ? (
                      <LazyImage
                        src={imageUrl}
                        alt={fabric.name || 'Fabric'}
                        className="w-full h-full object-cover pointer-events-none"
                        sizes="200px"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center pointer-events-none">
                        <ImageIcon size={28} className="text-gray-300" />
                      </div>
                    )}
                    {fabric.fabricTexture?.name && (
                      <div className="absolute top-1.5 left-1.5 bg-green-500 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-full shadow max-w-[90%] truncate pointer-events-none">
                        {fabric.fabricTexture.name}
                      </div>
                    )}
                  </div>
                  <p className="mt-2 text-[11px] font-semibold text-gray-800 line-clamp-2 leading-snug text-center px-0.5 pointer-events-none">
                    {fabric.name?.trim() || 'Untitled fabric'}
                  </p>
                </button>
              );
            })}
            <div ref={rightSpacerRef} className="shrink-0" style={{ width: 16 }} aria-hidden />
          </div>
        </div>
      )}
    </section>
  );
});


const FabricCollectionCard = React.memo(function FabricCollectionCard({
  collection,
  onOpen,
  onDelete,
  onEdit,
  onTogglePublic,
  isPublicSaving,
  onTogglePromoterVisibility,
  isPromoterVisibilitySaving,
  liteMode = false,
}) {
  const promotedFrom = collection.promotedFrom;
  const collectionImage = collection.image?.url || null;
  const totalProperties = collection.properties?.length || 0;
  const propertyEntries = (collection.properties || []).map((prop) => {
    if (prop.key && prop.value) return `${prop.key}: ${prop.value}`;
    return prop.key || prop.value || '';
  }).filter(Boolean);
  const fabricCount = collection.fabrics?.length || 0;
  const tags = collection.tags || [];
  const createdAt = collection.createdAt ? new Date(collection.createdAt) : null;

  const summary = propertyEntries.length
    ? propertyEntries.slice(0, 2).join(' • ')
    : 'No custom properties yet';
  const extraCount = propertyEntries.length > 2 ? propertyEntries.length - 2 : 0;

  return (
    <div
      className="group bg-white rounded-lg sm:rounded-xl border border-gray-200 hover:border-purple-300 hover:shadow-lg transition-all duration-300 overflow-hidden cursor-pointer"
      onClick={() => onOpen?.(collection._id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen?.(collection._id);
        }
      }}
    >
      <div className="relative aspect-square bg-gradient-to-br from-purple-50 via-pink-50 to-indigo-50 flex items-center justify-center overflow-hidden">
        {collectionImage ? (
          <LazyImage
            src={collectionImage}
            alt={collection.name}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
          />
        ) : (
          <div className="flex flex-col items-center justify-center w-full h-full px-4">
            <span className="text-lg sm:text-xl font-bold text-purple-600 text-center break-words">
              {collection.name}
            </span>
          </div>
        )}
        <div className="absolute top-1 left-1 sm:top-2 sm:left-2 bg-purple-600 text-white text-xs px-2 py-0.5 sm:px-3 sm:py-1 rounded-full font-semibold shadow">
          Collection
        </div>
        {fabricCount > 0 && (
          <div className="absolute top-1 right-1 sm:top-2 sm:right-2 bg-blue-500 text-white text-xs font-semibold px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full shadow">
            {fabricCount} fabric{fabricCount === 1 ? '' : 's'}
          </div>
        )}
        <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/25 opacity-0 group-hover:opacity-100 transition-all duration-300">
          <div className="bg-white/90 backdrop-blur-sm px-3 py-2 rounded-lg shadow-lg transform scale-90 group-hover:scale-100 transition-transform">
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
              <Layers size={16} />
              <span>View Details</span>
            </div>
          </div>
        </div>
      </div>

      <div className="p-3 sm:p-4 space-y-2">
        {promotedFrom?.label && (
          <p className="text-[10px] sm:text-xs font-semibold text-amber-900 bg-amber-50 border border-amber-100 rounded-md px-2 py-1">
            Approved promoter view · {promotedFrom.label}
          </p>
        )}
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-semibold text-gray-800 line-clamp-1 text-sm sm:text-base">{collection.name}</h3>
          <div className="flex items-center gap-2">
            {!liteMode && promotedFrom && typeof collection.promoterVisibility === 'boolean' && (
              <label
                className={`inline-flex items-center gap-2 select-none ${
                  isPromoterVisibilitySaving ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
                }`}
                title={collection.promoterVisibility ? 'Public' : 'Private'}
                onClick={(e) => e.stopPropagation()}
              >
                <span
                  className={`text-[10px] sm:text-xs font-semibold ${
                    collection.promoterVisibility ? 'text-emerald-700' : 'text-gray-500'
                  }`}
                >
                  {collection.promoterVisibility ? 'Public' : 'Private'}
                </span>
                <span className="relative inline-flex items-center">
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={!!collection.promoterVisibility}
                    disabled={!!isPromoterVisibilitySaving}
                    onChange={(e) => {
                      e.stopPropagation();
                      onTogglePromoterVisibility?.(collection, e.target.checked);
                    }}
                  />
                  <span
                    className={`w-9 h-5 rounded-full transition-colors ${
                      collection.promoterVisibility ? 'bg-emerald-500' : 'bg-gray-200'
                    }`}
                  />
                  <span
                    className={`absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                      collection.promoterVisibility ? 'translate-x-4' : 'translate-x-0'
                    }`}
                  />
                </span>
              </label>
            )}
            {!liteMode && !promotedFrom && typeof collection.isPublic === 'boolean' && (
              <label
                className={`inline-flex items-center gap-2 select-none ${
                  isPublicSaving ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
                }`}
                title={collection.isPublic ? 'Public' : 'Private'}
                onClick={(e) => e.stopPropagation()}
              >
                <span
                  className={`text-[10px] sm:text-xs font-semibold ${
                    collection.isPublic ? 'text-emerald-700' : 'text-gray-500'
                  }`}
                >
                  {collection.isPublic ? 'Public' : 'Private'}
                </span>
                <span className="relative inline-flex items-center">
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={!!collection.isPublic}
                    disabled={!!isPublicSaving}
                    onChange={(e) => {
                      e.stopPropagation();
                      onTogglePublic?.(collection, e.target.checked);
                    }}
                  />
                  <span
                    className={`w-9 h-5 rounded-full transition-colors ${
                      collection.isPublic ? 'bg-emerald-500' : 'bg-gray-200'
                    }`}
                  />
                  <span
                    className={`absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                      collection.isPublic ? 'translate-x-4' : 'translate-x-0'
                    }`}
                  />
                </span>
              </label>
            )}
            {!liteMode && !promotedFrom && onEdit && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(collection);
                }}
                className="text-xs text-purple-500 hover:text-purple-600 font-semibold"
              >
                Edit
              </button>
            )}
            {!liteMode && !promotedFrom && onDelete && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(collection);
                }}
                className="text-xs text-red-500 hover:text-red-600 font-semibold"
              >
                Delete
              </button>
            )}
          </div>
        </div>
        <p className="text-xs sm:text-sm text-gray-500 line-clamp-2">
          {summary}
          {extraCount > 0 && (
            <span className="text-purple-500 font-medium">{` • +${extraCount} more`}</span>
          )}
        </p>
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {tags.slice(0, 4).map(tag => (
              <span
                key={tag}
                className="text-[10px] sm:text-xs px-2 py-0.5 bg-purple-50 text-purple-600 rounded-full font-medium"
              >
                #{tag}
              </span>
            ))}
            {tags.length > 4 && (
              <span className="text-[10px] sm:text-xs text-purple-500 font-semibold">
                +{tags.length - 4}
              </span>
            )}
          </div>
        )}
        {!liteMode && (
          <div className="flex items-center justify-between text-[11px] sm:text-xs text-gray-400">
            <span>{createdAt ? `Created ${createdAt.toLocaleDateString()}` : 'Recently created'}</span>
            <span className="text-purple-500 font-medium">
              {fabricCount} fabric{fabricCount === 1 ? '' : 's'}
            </span>
          </div>
        )}
      </div>
    </div>
  );
});

// Filter helpers for My Fabrics

const COLOR_NAME_TO_HEX = {
  red: '#ef4444',
  orange: '#f97316',
  yellow: '#facc15',
  green: '#22c55e',
  sky: '#0ea5e9',
  blue: '#3b82f6',
  indigo: '#6366f1',
  purple: '#a855f7',
  pink: '#ec4899',
  gray: '#6b7280',
  grey: '#6b7280',
  black: '#111827',
  white: '#f9fafb',
  brown: '#92400e'
};

const getColorHex = (value) => {
  if (!value || typeof value !== 'string') return null;
  const v = value.trim().toLowerCase();
  if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(v)) return v;
  return COLOR_NAME_TO_HEX[v] || null;
};

const getColorLabel = (value) => {
  const hex = getColorHex(value);
  return hex ? `${value} (${hex})` : value;
};

const SEARCH_DEBOUNCE_MS = 500;

const MyProductsView = ({
  setProductDetails,
  setActiveView,
  showNotification,
  setPreSelectedProduct,
  openFabricCollectionDetail,
  externalEditProduct,
  onClearExternalEditProduct,
  initialProductsTab = 'fabrics',
  onProductsTabChange,
  openAiStudio,
  externalCatalogueFabric,
  onConsumedExternalCatalogueFabric,
  collectionsRefreshNonce = 0,
  liteMode = false,
}) => {
  const INITIAL_FILTERS = useMemo(
    () => ({
      fabricType: '',
      weaveOrKnit: '',
      opacity: '',
      stretch: '',
      color: ''
    }),
    []
  );

  const [products, setProducts] = useState([]);
  const [currentView, setCurrentView] = useState('list'); // 'list', 'add', 'edit', 'collection-create', 'collection-edit'
  const [editingProduct, setEditingProduct] = useState(null);
  const [catalogueFabric, setCatalogueFabric] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [visualSearchImageUrl, setVisualSearchImageUrl] = useState('');
  const [visualSearchImagePreview, setVisualSearchImagePreview] = useState('');
  const [isVisualSearchUploading, setIsVisualSearchUploading] = useState(false);
  const [showImageSearchOptions, setShowImageSearchOptions] = useState(false);
  const [isCameraCaptureOpen, setIsCameraCaptureOpen] = useState(false);
  const [isCameraStarting, setIsCameraStarting] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filterDraft, setFilterDraft] = useState(INITIAL_FILTERS);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [visibilityLoadingId, setVisibilityLoadingId] = useState(null);
  const [analysisLoadingId, setAnalysisLoadingId] = useState(null);
  const [collectionPublicSavingId, setCollectionPublicSavingId] = useState(null);
  const [promotedCollectionVisibilitySavingId, setPromotedCollectionVisibilitySavingId] = useState(null);
  const observerTarget = useRef(null);
  const collectionObserverRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const collectionSearchTimeoutRef = useRef(null);
  const visualSearchInputRef = useRef(null);
  const visualSearchCameraInputRef = useRef(null);
  const imageSearchMenuRef = useRef(null);
  const cameraVideoRef = useRef(null);
  const cameraCanvasRef = useRef(null);
  const { addNotification } = useNotifications();
  const [fabricCollectionForm, setFabricCollectionForm] = useState(INITIAL_FABRIC_COLLECTION_FORM);
  const [fabricCollections, setFabricCollections] = useState([]);
  const [promotedCollections, setPromotedCollections] = useState([]);
  const [promotedFabrics, setPromotedFabrics] = useState([]);
  const [promotedCatalogLoading, setPromotedCatalogLoading] = useState(false);
  const [collectionsLoading, setCollectionsLoading] = useState(false);
  const [collectionSearchInput, setCollectionSearchInput] = useState('');
  const [collectionSearchQuery, setCollectionSearchQuery] = useState('');
  const [collectionTagInput, setCollectionTagInput] = useState('');
  const [isSavingFabricCollection, setIsSavingFabricCollection] = useState(false);
  const [editingCollection, setEditingCollection] = useState(null);
  const [activeProductsTab, setActiveProductsTab] = useState(initialProductsTab); // 'fabrics' | 'collections'
  const [uploadingFabricImages, setUploadingFabricImages] = useState([]);
  const [uploadingFabricPreviewUrls, setUploadingFabricPreviewUrls] = useState([]);
  const uploadingFabricPreviewUrlsRef = useRef([]);

  const clearUploadingFabricPreviews = useCallback(() => {
    uploadingFabricPreviewUrlsRef.current.forEach((url) => {
      try {
        URL.revokeObjectURL(url);
      } catch (_) {
        /* ignore */
      }
    });
    uploadingFabricPreviewUrlsRef.current = [];
    setUploadingFabricPreviewUrls([]);
  }, []);

  useEffect(() => {
    return () => {
      uploadingFabricPreviewUrlsRef.current.forEach((url) => {
        try {
          URL.revokeObjectURL(url);
        } catch (_) {
          /* ignore */
        }
      });
    };
  }, []);
  const [uploadingFabricsProgress, setUploadingFabricsProgress] = useState({});
  const [uploadedFabrics, setUploadedFabrics] = useState([]); // Store uploaded fabric products to show as thumbnails
  const [followersCount, setFollowersCount] = useState(null);
  const [fabricTypes, setFabricTypes] = useState([]);
  const [weaveKnitValues, setWeaveKnitValues] = useState([]);
  const [opacityValues, setOpacityValues] = useState([]);
  const [stretchValues, setStretchValues] = useState([]);
  const [colorValues, setColorValues] = useState([]);

  useEffect(() => {
    if (!externalCatalogueFabric) return;
    setCatalogueFabric(externalCatalogueFabric);
    onConsumedExternalCatalogueFabric?.();
  }, [externalCatalogueFabric]);

  useEffect(() => {
    setActiveProductsTab(initialProductsTab);
  }, [initialProductsTab]);

  // Fetch followers count
  useEffect(() => {
    const fetchFollowersCount = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        const res = await getFollowersCount(token);
        setFollowersCount(res.data?.data?.count ?? 0);
      } catch {
        setFollowersCount(0);
      }
    };
    fetchFollowersCount();
  }, []);

  // Fetch fabric types for filter dropdown
  useEffect(() => {
    const fetchFabricTypes = async () => {
      try {
        const types = await getFabricTypes();
        setFabricTypes(Array.isArray(types) ? types : []);
      } catch {
        setFabricTypes([]);
      }
    };
    fetchFabricTypes();
  }, []);

  // Fetch weave/knit values for filter dropdown
  useEffect(() => {
    const fetchWeaveKnit = async () => {
      try {
        const values = await getWeaveKnitValues();
        setWeaveKnitValues(Array.isArray(values) ? values : []);
      } catch {
        setWeaveKnitValues([]);
      }
    };
    fetchWeaveKnit();
  }, []);

  // Fetch opacity values for filter dropdown
  useEffect(() => {
    const fetchOpacity = async () => {
      try {
        const values = await getOpacityValues();
        setOpacityValues(Array.isArray(values) ? values : []);
      } catch {
        setOpacityValues([]);
      }
    };
    fetchOpacity();
  }, []);

  // Fetch stretch values for filter dropdown
  useEffect(() => {
    const fetchStretch = async () => {
      try {
        const values = await getStretchValues();
        setStretchValues(Array.isArray(values) ? values : []);
      } catch {
        setStretchValues([]);
      }
    };
    fetchStretch();
  }, []);

  // Fetch color values for filter section
  useEffect(() => {
    const fetchColors = async () => {
      try {
        const values = await getColorValues();
        setColorValues(Array.isArray(values) ? values : []);
      } catch {
        setColorValues([]);
      }
    };
    fetchColors();
  }, []);

  const handleFabricUpdated = useCallback((updatedFabric) => {
    if (!updatedFabric?._id) return;

    setProducts(prev =>
      prev.map(product => (product._id === updatedFabric._id ? { ...product, ...updatedFabric } : product))
    );

    setCatalogueFabric(prev => (prev?._id === updatedFabric._id ? { ...prev, ...updatedFabric } : prev));
    setEditingProduct(prev => (prev?._id === updatedFabric._id ? { ...prev, ...updatedFabric } : prev));
  }, []);

  const availableModalFabrics = useMemo(
    () => products.filter(fabric => !fabric.fabricCollection),
    [products]
  );

  // Check if user is admin
  useEffect(() => {
    const userRole = localStorage.getItem('role')?.toLowerCase();
    setIsAdmin(userRole === 'admin');
  }, []);

  useEffect(() => {
    if (liteMode || !externalEditProduct) return;
    setEditingProduct(externalEditProduct);
    setCurrentView('edit');
    onClearExternalEditProduct?.();
  }, [externalEditProduct, onClearExternalEditProduct, liteMode]);

  // Fetch products with pagination
  const fetchProducts = useCallback(async (page = 1, append = false) => {
    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
    }
    
    try {
      const trimmedSearchQuery = (searchQuery || '').trim();
      let response;
      if (visualSearchImageUrl) {
        response = await visualSearchProducts({
          imageUrl: visualSearchImageUrl,
          page,
          limit: 20,
          filters,
          candidateLimit: 250,
          maxNewEmbeddings: 10,
          strictMode: true
        });
      } else if (trimmedSearchQuery) {
        response = await semanticSearchProducts({
          queryText: trimmedSearchQuery,
          page,
          limit: 20,
          filters,
          candidateLimit: 250,
          maxNewEmbeddings: 10
        });
      } else {
        response = await getProducts(page, 20, trimmedSearchQuery, filters);
      }
      const { products: fetchedProducts, pagination, scores: visualScores } = response || {};
      const isVisualSearch = Boolean(visualSearchImageUrl);

      if (append) {
        setProducts(prev => {
          const enriched = mergeVisualSearchScores(fetchedProducts, visualScores, isVisualSearch);
          const existingIds = new Set(prev.map(p => String(p._id)));
          const newProducts = enriched.filter(p => !existingIds.has(String(p._id)));
          return [...prev, ...newProducts];
        });
      } else {
        setProducts(mergeVisualSearchScores(fetchedProducts, visualScores, isVisualSearch));
      }
      
      setCurrentPage(pagination?.currentPage ?? 1);
      setHasNextPage(Boolean(pagination?.hasNextPage));
      } catch (error) {
        addNotification({ message: 'Failed to fetch products', type: 'error' });
      } finally {
        setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [searchQuery, visualSearchImageUrl, filters, addNotification]);

  useEffect(() => {
    if (!showImageSearchOptions) return;
    const handleOutsideClick = (event) => {
      if (imageSearchMenuRef.current && !imageSearchMenuRef.current.contains(event.target)) {
        setShowImageSearchOptions(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [showImageSearchOptions]);

  useEffect(() => {
    if (!cameraVideoRef.current || !cameraStream) return;
    cameraVideoRef.current.srcObject = cameraStream;
  }, [cameraStream]);

  useEffect(() => {
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [cameraStream]);

  const clearVisualSearch = useCallback(() => {
    if (visualSearchImagePreview?.startsWith('blob:')) {
      URL.revokeObjectURL(visualSearchImagePreview);
    }
    setVisualSearchImagePreview('');
    setVisualSearchImageUrl('');
    if (visualSearchInputRef.current) {
      visualSearchInputRef.current.value = '';
    }
  }, [visualSearchImagePreview]);

  const processVisualSearchFile = useCallback(async (file) => {
    if (!file) return;

    if (!file.type?.startsWith('image/')) {
      addNotification({ message: 'Please select an image file', type: 'error' });
      return;
    }

    setIsVisualSearchUploading(true);
    try {
      const compressedFile = await compressImage(file, { maxWidth: 1280, maxHeight: 1280, quality: 0.85 });
      const previewUrl = URL.createObjectURL(compressedFile);
      setVisualSearchImagePreview(prev => {
        if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev);
        return previewUrl;
      });

      const uploadResult = await uploadImageToS3(compressedFile, {
        folder: 'visual-search',
        fileName: `visual-search-${Date.now()}-${compressedFile.name}`
      });

      const uploadedUrl = uploadResult?.url || '';
      if (!uploadedUrl) {
        throw new Error('Failed to upload search image');
      }

      setSearchInput('');
      setSearchQuery('');
      setVisualSearchImageUrl(uploadedUrl);
      addNotification({ message: 'Image search enabled', type: 'success' });
    } catch (error) {
      console.error('Error preparing visual search image:', error);
      addNotification({ message: error?.message || 'Failed to process search image', type: 'error' });
      clearVisualSearch();
    } finally {
      setIsVisualSearchUploading(false);
      if (visualSearchInputRef.current) {
        visualSearchInputRef.current.value = '';
      }
      if (visualSearchCameraInputRef.current) {
        visualSearchCameraInputRef.current.value = '';
      }
    }
  }, [addNotification, clearVisualSearch]);

  const handleVisualSearchImageSelect = useCallback(async (event) => {
    const file = event.target.files?.[0];
    await processVisualSearchFile(file);
  }, [processVisualSearchFile]);

  const handleVisualSearchCameraCapture = useCallback(async (event) => {
    const file = event.target.files?.[0];
    await processVisualSearchFile(file);
  }, [processVisualSearchFile]);

  const closeCameraCapture = useCallback(() => {
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
    }
    setCameraStream(null);
    setIsCameraCaptureOpen(false);
    setIsCameraStarting(false);
  }, [cameraStream]);

  const openCameraCapture = useCallback(async () => {
    if (!navigator?.mediaDevices?.getUserMedia) {
      visualSearchCameraInputRef.current?.click();
      return;
    }

    setIsCameraStarting(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
        audio: false
      });
      setCameraStream(stream);
      setIsCameraCaptureOpen(true);
    } catch (error) {
      console.error('Camera access failed, falling back to file input:', error);
      addNotification({ message: 'Camera unavailable, opening file picker instead', type: 'info' });
      visualSearchCameraInputRef.current?.click();
    } finally {
      setIsCameraStarting(false);
    }
  }, [addNotification]);

  const captureCameraPhoto = useCallback(async () => {
    if (!cameraVideoRef.current || !cameraCanvasRef.current) {
      addNotification({ message: 'Camera is not ready yet', type: 'error' });
      return;
    }

    const video = cameraVideoRef.current;
    const canvas = cameraCanvasRef.current;
    const width = video.videoWidth || 1280;
    const height = video.videoHeight || 720;
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      addNotification({ message: 'Failed to capture image', type: 'error' });
      return;
    }

    ctx.drawImage(video, 0, 0, width, height);

    const capturedFile = await new Promise((resolve) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          resolve(null);
          return;
        }
        resolve(new File([blob], `camera-capture-${Date.now()}.jpg`, { type: 'image/jpeg' }));
      }, 'image/jpeg', 0.92);
    });

    if (!capturedFile) {
      addNotification({ message: 'Failed to capture photo', type: 'error' });
      return;
    }

    closeCameraCapture();
    await processVisualSearchFile(capturedFile);
  }, [addNotification, closeCameraCapture, processVisualSearchFile]);

  const openImageSearchOption = useCallback((mode) => {
    setShowImageSearchOptions(false);
    if (mode === 'camera') {
      openCameraCapture();
      return;
    }
    visualSearchInputRef.current?.click();
  }, [openCameraCapture]);

  const fetchFabricCollections = useCallback(async (options = {}) => {
    const skipCache = Boolean(options.skipCache);
    setCollectionsLoading(true);
    try {
      // Lite mode needs fabric items for horizontal rows; search also needs fabrics for matching.
      const includeFabrics = liteMode || collectionSearchQuery.length > 0;
      const collections = await getFabricCollections(includeFabrics, collectionSearchQuery, skipCache);
      setFabricCollections(collections || []);
    } catch (error) {
      console.error('Failed to fetch fabric collections:', error);
      addNotification({ message: 'Failed to fetch fabric collections', type: 'error' });
    } finally {
      setCollectionsLoading(false);
    }
  }, [addNotification, collectionSearchQuery, liteMode]);

  const loadPromotedCatalog = useCallback(async () => {
    setPromotedCatalogLoading(true);
    try {
      const vendors = await getPromotedVendors();
      if (!vendors.length) {
        setPromotedFabrics([]);
        setPromotedCollections([]);
        return;
      }

      const fabricSearch = visualSearchImageUrl ? '' : (searchQuery || '').trim();
      const collectionSearch = (collectionSearchQuery || '').trim();
      const allFabrics = [];
      const allCollections = [];

      for (const entry of vendors) {
        const u = entry.user;
        const vendorId = u?.id || u?._id;
        if (!vendorId) continue;
        const label = u?.brandName || u?.name || 'Vendor';
        const promotedFrom = { label, phone: u?.phone, id: u?.id };

        let page = 1;
        let hasNext = true;
        while (hasNext && page <= 10) {
          const data = await getPromotedVendorProducts(vendorId, page, 50, fabricSearch);
          const prods = data?.products || [];
          prods.forEach((p) => allFabrics.push({ ...p, promotedFrom }));
          hasNext = Boolean(data?.pagination?.hasNextPage);
          page += 1;
        }

        page = 1;
        hasNext = true;
        while (hasNext && page <= 10) {
          const colData = await getPromotedVendorCollections(vendorId, page, 50, collectionSearch);
          const cols = colData?.collections || [];
          cols.forEach((c) => allCollections.push({ ...c, promotedFrom }));
          hasNext = Boolean(colData?.pagination?.hasNextPage);
          page += 1;
        }
      }

      setPromotedFabrics(allFabrics);
      setPromotedCollections(allCollections);
    } catch (error) {
      console.error('Failed to load promoted vendor catalog:', error);
      setPromotedFabrics([]);
      setPromotedCollections([]);
    } finally {
      setPromotedCatalogLoading(false);
    }
  }, [visualSearchImageUrl, searchQuery, collectionSearchQuery]);

  const handleActiveProductsTabChange = useCallback(
    (tab) => {
      setActiveProductsTab(tab);
      onProductsTabChange?.(tab);
      if (tab === 'collections') {
        fetchFabricCollections({ skipCache: true });
        loadPromotedCatalog();
      }
    },
    [onProductsTabChange, fetchFabricCollections, loadPromotedCatalog]
  );

  // Debounce search input
  useEffect(() => {
    if (activeProductsTab !== 'fabrics') return;

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    const nextQuery = (searchInput || '').trim();
    searchTimeoutRef.current = setTimeout(() => {
      setSearchQuery(prev => (prev === nextQuery ? prev : nextQuery));
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchInput, activeProductsTab]);

  // Initial load and when search changes
  useEffect(() => {
    setCurrentPage(1);
    fetchProducts(1, false);
  }, [searchQuery, visualSearchImageUrl, fetchProducts]);

  useEffect(() => {
    return () => {
      if (visualSearchImagePreview?.startsWith('blob:')) {
        URL.revokeObjectURL(visualSearchImagePreview);
      }
    };
  }, [visualSearchImagePreview]);

  const appliedFilterCount = useMemo(
    () => Object.values(filters || {}).filter(v => typeof v === 'string' ? v.trim().length > 0 : !!v).length,
    [filters]
  );

  const applyFilters = () => {
    setFilters(filterDraft);
    setShowFilters(false);
  };

  const clearFilters = () => {
    setFilterDraft(INITIAL_FILTERS);
    setFilters(INITIAL_FILTERS);
    setShowFilters(false);
  };

  useEffect(() => {
    if (collectionSearchTimeoutRef.current) {
      clearTimeout(collectionSearchTimeoutRef.current);
    }

    collectionSearchTimeoutRef.current = setTimeout(() => {
      setCollectionSearchQuery(collectionSearchInput.trim());
    }, 400);

    return () => {
      if (collectionSearchTimeoutRef.current) {
        clearTimeout(collectionSearchTimeoutRef.current);
      }
    };
  }, [collectionSearchInput]);

  useEffect(() => {
    fetchFabricCollections();
  }, [fetchFabricCollections]);

  useEffect(() => {
    if (activeProductsTab !== 'collections') return;
    fetchFabricCollections({ skipCache: true });
  }, [collectionsRefreshNonce, activeProductsTab, fetchFabricCollections]);

  useEffect(() => {
    loadPromotedCatalog();
  }, [loadPromotedCatalog]);

  const displayCollections = useMemo(() => {
    const all = [...(fabricCollections || []), ...(promotedCollections || [])];
    // Only show top-level collections in the tab (child collections appear inside the parent detail view).
    return all.filter((c) => !c?.parent);
  }, [fabricCollections, promotedCollections]);

  const childCollectionsByParentId = useMemo(() => {
    const map = new Map();
    for (const c of fabricCollections || []) {
      if (!c?.parent || !c?._id) continue;
      const parentId = String(c.parent);
      if (!map.has(parentId)) map.set(parentId, []);
      map.get(parentId).push(c);
    }
    return map;
  }, [fabricCollections]);

  const fabricLookupById = useMemo(() => {
    const map = new Map();
    for (const f of [...(products || []), ...(promotedFabrics || [])]) {
      if (f?._id) map.set(String(f._id), f);
    }
    return map;
  }, [products, promotedFabrics]);

  const resolveCollectionFabrics = useCallback(
    (collection) => {
      const raw = Array.isArray(collection?.fabrics) ? collection.fabrics : [];
      const seen = new Set();
      const resolved = [];
      for (const ref of raw) {
        const populated =
          ref && typeof ref === 'object' && ref._id && (ref.image || ref.images || ref.name)
            ? ref
            : null;
        const id = String(populated?._id || ref?._id || ref || '').trim();
        if (!id || seen.has(id)) continue;
        seen.add(id);
        const fabric = populated || fabricLookupById.get(id);
        if (fabric) resolved.push(fabric);
      }
      return resolved;
    },
    [fabricLookupById]
  );

  const ownProductIdSet = useMemo(() => new Set((products || []).map((p) => String(p._id))), [products]);

  const displayPromotedFabrics = useMemo(
    () => (promotedFabrics || []).filter((f) => f?._id && !ownProductIdSet.has(String(f._id))),
    [promotedFabrics, ownProductIdSet]
  );

  const displayAllFabrics = useMemo(
    () => [...(products || []), ...displayPromotedFabrics],
    [products, displayPromotedFabrics]
  );

  // Infinite scroll observer
  useEffect(() => {
    if (!hasNextPage || isLoadingMore || isLoading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isLoadingMore && !isLoading) {
          fetchProducts(currentPage + 1, true);
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasNextPage, isLoadingMore, isLoading, currentPage, fetchProducts]);

  useEffect(() => {
    const isCollectionView = currentView === 'collection-create' || currentView === 'collection-edit';
    if (!isCollectionView || !hasNextPage || isLoadingMore || isLoading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isLoadingMore && !isLoading) {
          fetchProducts(currentPage + 1, true);
        }
      },
      { threshold: 0.25 }
    );

    const current = collectionObserverRef.current;
    if (current) {
      observer.observe(current);
    }

    return () => {
      if (current) {
        observer.unobserve(current);
      }
    };
  }, [currentView, hasNextPage, isLoadingMore, isLoading, currentPage, fetchProducts]);

  const handleDelete = useCallback(async (productId) => {
    if (promotedFabrics.some((p) => String(p._id) === String(productId))) {
      addNotification({ message: "You cannot delete another vendor's fabric from your promoted catalogue.", type: 'info' });
      return;
    }
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await deleteProduct(productId);
        setProducts(prev => prev.filter(p => p._id !== productId));
        addNotification({ message: 'Product deleted successfully!', type: 'success' });
        // Refresh the list to update pagination
        fetchProducts(currentPage, false);
      } catch (error) {
        addNotification({ message: 'Failed to delete product', type: 'error' });
      }
    }
  }, [addNotification, fetchProducts, currentPage, promotedFabrics]);

  const handleRunFabricAnalysis = useCallback(async (fabric) => {
    if (!fabric?._id) return;

    const imageUrl =
      fabric.image ||
      fabric.images?.original?.s3Url ||
      fabric.images?.original?.imgbbUrl ||
      fabric.images?.original?.url ||
      '';

    if (!imageUrl) {
      addNotification({ message: 'No fabric image available for analysis', type: 'error' });
      return;
    }

    setAnalysisLoadingId(fabric._id);
    try {
      const response = await analyzeFabric(imageUrl, {
        name: fabric.name,
        description: fabric.description,
        category: fabric.category,
        productType: fabric.productType,
        targetAudience: fabric.targetAudience,
        tags: fabric.tags,
        properties: fabric.properties,
        fabricTexture: fabric.fabricTexture,
        colorVariants: fabric.colorVariants
      });
      const analysis = response?.data?.analysis || null;
      const searchFromAnalysis = response?.data?.search;

      if (!analysis) {
        throw new Error('Fabric analysis response is empty');
      }

      const updatePayload = {
        fabricAnalysis: analysis
      };
      const gsmWeight = analysis?.fabric_analysis?.gsm_weight;
      if (gsmWeight) {
        updatePayload.fabricWeight = gsmWeight;
      }
      if (searchFromAnalysis && typeof searchFromAnalysis === 'object') {
        updatePayload.search = searchFromAnalysis;
      }

      const updatedProduct = await updateProduct(fabric._id, updatePayload);

      setProducts(prev =>
        prev.map(item => (item._id === fabric._id ? { ...item, ...updatedProduct } : item))
      );
      setCatalogueFabric(prev =>
        prev?._id === fabric._id ? { ...prev, ...updatedProduct } : prev
      );

      addNotification({ message: 'Fabric analysis completed and saved', type: 'success' });
    } catch (error) {
      console.error('Error running fabric analysis:', error);
      addNotification({
        message: error?.message || 'Failed to run fabric analysis',
        type: 'error'
      });
    } finally {
      setAnalysisLoadingId(null);
    }
  }, [addNotification]);

  // Handle edit product
  const handleEdit = useCallback((product) => {
    if (product?.promotedFrom) {
      addNotification({ message: 'Edit is only available for fabrics you own.', type: 'info' });
      return;
    }
    setEditingProduct(product);
    setCurrentView('edit');
  }, [addNotification]);

  const handleProductUpdated = (updatedProduct) => {
    setEditingProduct(null);
    setCurrentView('list');
    addNotification({ message: 'Product updated successfully!', type: 'success' });
    // Refresh the list to show updated product
    fetchProducts(currentPage, false);
  };

  const handleEditClose = () => {
    setEditingProduct(null);
    setCurrentView('list');
  };

  const handleAddClose = () => {
    setCurrentView('list');
  };

  const handleProductAdded = (newProduct) => {
    setCurrentView('list');
    addNotification({ message: 'Product added successfully!', type: 'success' });
    // Refresh the list to show the new product
    fetchProducts(1, false);
  };

  const resetFabricCollectionForm = useCallback(() => {
    setFabricCollectionForm(prev => {
      if (prev.imagePreview && typeof window !== 'undefined' && prev.imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(prev.imagePreview);
      }
      return INITIAL_FABRIC_COLLECTION_FORM;
    });
    setCollectionTagInput('');
  }, []);

  const handleOpenFabricCollectionPage = useCallback(() => {
    resetFabricCollectionForm();
    setEditingCollection(null);
    setCurrentView('collection-create');
  }, [resetFabricCollectionForm]);

  const handleCloseFabricCollectionPage = useCallback(() => {
    // Clear uploaded fabrics when closing
    setUploadedFabrics([]);
    clearUploadingFabricPreviews();
    setUploadingFabricImages([]);
    setUploadingFabricsProgress({});
    resetFabricCollectionForm();
    setEditingCollection(null);
    setCurrentView('list');
  }, [resetFabricCollectionForm, clearUploadingFabricPreviews]);

  // Native back button: close the full-screen collection editor overlay.
  useBackButton(
    currentView === 'collection-create' || currentView === 'collection-edit',
    handleCloseFabricCollectionPage
  );

  const handleFabricCollectionNameChange = (value) => {
    setFabricCollectionForm(prev => ({
      ...prev,
      name: value
    }));
  };

  const handleFabricCollectionImageSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const previewUrl = typeof window !== 'undefined' ? URL.createObjectURL(file) : null;

    setFabricCollectionForm(prev => {
      if (prev.imagePreview && typeof window !== 'undefined' && prev.imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(prev.imagePreview);
      }

      return {
        ...prev,
        imageFile: file,
        imagePreview: previewUrl,
        removeImage: false
      };
    });
  };

  const handleFabricCollectionImageClear = () => {
    setFabricCollectionForm(prev => {
      if (prev.imagePreview && typeof window !== 'undefined' && prev.imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(prev.imagePreview);
      }

      return {
        ...prev,
        imageFile: null,
        imagePreview: null,
        removeImage: true
      };
    });
  };

  const handleFabricCollectionPropertyChange = (index, field, value) => {
    setFabricCollectionForm(prev => {
      const updatedProperties = prev.properties.map((property, idx) => {
        if (idx === index) {
          return {
            ...property,
            [field]: value
          };
        }
        return property;
      });

      return {
        ...prev,
        properties: updatedProperties
      };
    });
  };

  const handleFabricCollectionTagAdd = useCallback(() => {
    const value = collectionTagInput.trim();
    if (!value) return;

    setFabricCollectionForm(prev => {
      if (prev.tags.includes(value) || prev.tags.length >= 30) {
        return prev;
      }
      return {
        ...prev,
        tags: [...prev.tags, value]
      };
    });
    setCollectionTagInput('');
  }, [collectionTagInput]);

  const handleFabricCollectionTagRemove = useCallback((tagToRemove) => {
    setFabricCollectionForm(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  }, []);

  const handleFabricCollectionTagKeyDown = useCallback((event) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      handleFabricCollectionTagAdd();
    } else if (event.key === 'Backspace' && !collectionTagInput) {
      setFabricCollectionForm(prev => ({
        ...prev,
        tags: prev.tags.slice(0, -1)
      }));
    }
  }, [collectionTagInput, handleFabricCollectionTagAdd]);

  const handleAddFabricCollectionProperty = () => {
    setFabricCollectionForm(prev => ({
      ...prev,
      properties: [...prev.properties, { key: '', value: '' }]
    }));
  };

  const handleRemoveFabricCollectionProperty = (index) => {
    setFabricCollectionForm(prev => {
      if (prev.properties.length === 1) {
        return {
          ...prev,
          properties: [{ key: '', value: '' }]
        };
      }

      const updatedProperties = prev.properties.filter((_, idx) => idx !== index);

      return {
        ...prev,
        properties: updatedProperties
      };
    });
  };

  const handleToggleFabricInCollection = (fabricId) => {
    setFabricCollectionForm(prev => {
      const selected = prev.selectedFabrics.includes(fabricId)
        ? prev.selectedFabrics.filter(id => id !== fabricId)
        : [...prev.selectedFabrics, fabricId];
      return {
        ...prev,
        selectedFabrics: selected
      };
    });
  };

  const handleFabricImagesUpload = async (files) => {
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);
    const baseTime = Date.now();
    const fileIds = fileArray.map((_, i) => `${baseTime}-${i}`);
    const multiUpload = fileArray.length > 1;

    setUploadingFabricImages(fileArray);
    clearUploadingFabricPreviews();
    const previewUrls = fileArray.map((file) => URL.createObjectURL(file));
    uploadingFabricPreviewUrlsRef.current = previewUrls;
    setUploadingFabricPreviewUrls(previewUrls);
    setUploadingFabricsProgress({});

    const processOneFabricFile = async (file, fileId) => {
      try {
        setUploadingFabricsProgress((prev) => ({
          ...prev,
          [fileId]: {
            progress: 0,
            status: isHeicFile(file) ? 'converting' : 'uploading',
          },
        }));

        const fileToUpload = isHeicFile(file) ? await normalizeImageFile(file) : file;

        setUploadingFabricsProgress((prev) => ({
          ...prev,
          [fileId]: { progress: 10, status: 'uploading' }
        }));

        const uploadRes = await uploadImageToS3(fileToUpload, {
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 70) / progressEvent.total) + 10;
            setUploadingFabricsProgress((prev) => ({
              ...prev,
              [fileId]: { progress, status: 'uploading' }
            }));
          }
        });

        const imageUrl = uploadRes.url || uploadRes.s3Url;

        const finalImageUrl = imageUrl;

        const productName = file.name.replace(/\.[^/.]+$/, '') || `Fabric ${Date.now()}`;
        const productDescription = 'Fabric material uploaded from collection';

        setUploadingFabricsProgress((prev) => ({
          ...prev,
          [fileId]: { progress: 90, status: 'analyzing' }
        }));

        let fabricAnalysisResult = null;
        let fabricAnalysisSearch = null;
        try {
          const analysisResponse = await analyzeFabric(finalImageUrl, {
            name: productName,
            description: productDescription,
            category: 'fabric',
            productType: 'Fabric Material',
            targetAudience: 'General Audience'
          });
          if (analysisResponse?.success && analysisResponse.data?.analysis) {
            fabricAnalysisResult = analysisResponse.data.analysis;
            fabricAnalysisSearch = analysisResponse.data.search || null;
          }
        } catch (analysisErr) {
          console.warn('Fabric analysis failed for collection upload (product will still be created):', analysisErr);
        }

        setUploadingFabricsProgress((prev) => ({
          ...prev,
          [fileId]: { progress: 96, status: 'creating' }
        }));

        const newProduct = {
          name: productName,
          description: productDescription,
          category: 'fabric',
          productType: 'Fabric Material',
          targetAudience: 'General Audience',
          image: finalImageUrl,
          images: {
            original: {
              s3Url: finalImageUrl
            }
          }
        };
        if (fabricAnalysisResult) {
          newProduct.fabricAnalysis = fabricAnalysisResult;
          const gsm = fabricAnalysisResult?.fabric_analysis?.gsm_weight;
          if (gsm) {
            newProduct.fabricWeight = gsm;
          }
        }
        if (fabricAnalysisSearch && typeof fabricAnalysisSearch === 'object') {
          newProduct.search = fabricAnalysisSearch;
        }

        const createdProduct = await createProduct(newProduct);

        setUploadingFabricsProgress((prev) => ({
          ...prev,
          [fileId]: { progress: 100, status: 'completed' }
        }));

        setUploadedFabrics((prev) => {
          if (prev.some((p) => p._id === createdProduct._id)) return prev;
          return [...prev, createdProduct];
        });

        setProducts((prev) => {
          if (prev.some((p) => p._id === createdProduct._id)) return prev;
          return [createdProduct, ...prev];
        });

        setFabricCollectionForm((prev) => ({
          ...prev,
          selectedFabrics: prev.selectedFabrics.includes(createdProduct._id)
            ? prev.selectedFabrics
            : [...prev.selectedFabrics, createdProduct._id]
        }));

        return { ok: true, createdProduct };
      } catch (error) {
        console.error(`Error processing fabric image ${file.name}:`, error);
        setUploadingFabricsProgress((prev) => ({
          ...prev,
          [fileId]: { progress: 0, status: 'error', error: error.message }
        }));
        return { ok: false };
      }
    };

    try {
      const results = await Promise.all(
        fileArray.map((file, i) => processOneFabricFile(file, fileIds[i]))
      );
      const createdProductIds = results.filter((r) => r.ok && r.createdProduct).map((r) => r.createdProduct._id);

      await fetchProducts(1, false);

      const failCount = fileArray.length - createdProductIds.length;
      if (createdProductIds.length === 0) {
        if (showNotification) {
          showNotification('No fabrics could be uploaded. Check errors above.', 'error');
        } else {
          addNotification?.({ message: 'No fabrics could be uploaded.', type: 'error' });
        }
      } else if (showNotification) {
        const suffix = failCount > 0 ? ` (${failCount} failed)` : '';
        showNotification(
          `Successfully uploaded ${createdProductIds.length} fabric material(s) and added to collection.${suffix}`,
          failCount > 0 ? 'info' : 'success'
        );
      } else {
        addNotification?.({
          message:
            failCount > 0
              ? `Uploaded ${createdProductIds.length} fabric(s); ${failCount} failed.`
              : `Successfully uploaded ${createdProductIds.length} fabric material(s) and added to collection.`,
          type: failCount > 0 ? 'info' : 'success'
        });
      }
    } catch (error) {
      console.error('Error uploading fabric images:', error);
      if (showNotification) {
        showNotification('Failed to upload some fabric images.', 'error');
      } else {
        addNotification?.({ message: 'Failed to upload some fabric images.', type: 'error' });
      }
    } finally {
      setTimeout(() => {
        clearUploadingFabricPreviews();
        setUploadingFabricImages([]);
        setUploadingFabricsProgress({});
      }, 2000);
    }
  };

  const handleSaveFabricCollection = async () => {
    if (!fabricCollectionForm.name.trim()) {
      if (showNotification) {
        showNotification('Please enter a collection name.', 'error');
      } else {
        addNotification?.({ message: 'Please enter a collection name.', type: 'error' });
      }
      return;
    }

    const preparedProperties = fabricCollectionForm.properties
      .filter(property => property.key.trim() || property.value.trim())
      .map(property => ({
        key: property.key.trim(),
        value: property.value.trim()
      }));

    setIsSavingFabricCollection(true);
    try {
      if (editingCollection) {
      const updatePayload = {
          name: fabricCollectionForm.name.trim(),
          properties: preparedProperties,
        fabrics: fabricCollectionForm.selectedFabrics,
        tags: fabricCollectionForm.tags
        };

        if (fabricCollectionForm.imageFile) {
          updatePayload.imageFile = fabricCollectionForm.imageFile;
        } else if (fabricCollectionForm.removeImage) {
          updatePayload.removeImage = true;
        }

        await updateFabricCollection(editingCollection._id, updatePayload);
        if (showNotification) {
          showNotification('Fabric collection updated successfully.', 'success');
        } else {
          addNotification?.({ message: 'Fabric collection updated successfully.', type: 'success' });
        }
      } else {
        const createPayload = {
          name: fabricCollectionForm.name.trim(),
          properties: preparedProperties,
          fabrics: fabricCollectionForm.selectedFabrics,
          tags: fabricCollectionForm.tags
        };

        if (fabricCollectionForm.imageFile) {
          createPayload.imageFile = fabricCollectionForm.imageFile;
        }

        await createFabricCollection(createPayload);
        if (showNotification) {
          showNotification('Fabric collection saved successfully.', 'success');
        } else {
          addNotification?.({ message: 'Fabric collection saved successfully.', type: 'success' });
        }
      }

      await fetchFabricCollections();
      await loadPromotedCatalog();
      await fetchProducts(1, false);
      // Clear uploaded fabrics after successful save
      setUploadedFabrics([]);
      clearUploadingFabricPreviews();
      setUploadingFabricImages([]);
      setUploadingFabricsProgress({});
      handleCloseFabricCollectionPage();
    } catch (error) {
      console.error('Error saving fabric collection:', error);
      const errorMessage = error?.response?.data?.message || 'Failed to save fabric collection.';
      if (showNotification) {
        showNotification(errorMessage, 'error');
      } else {
        addNotification?.({ message: errorMessage, type: 'error' });
      }
    } finally {
      setIsSavingFabricCollection(false);
    }
  };

  const handleEditCollection = (collection) => {
    if (!collection) return;
    if (collection.promotedFrom) {
      addNotification?.({ message: 'You can view this collection but not edit it.', type: 'info' });
      return;
    }
    setEditingCollection(collection);
    setFabricCollectionForm({
      name: collection.name || '',
      imageFile: null,
      imagePreview: collection.image?.url || null,
      properties: (collection.properties || []).map(prop => ({
        key: prop.key || '',
        value: prop.value || ''
      })),
      selectedFabrics: (collection.fabrics || []).map(fabric => fabric._id || fabric),
      removeImage: false,
      tags: (collection.tags || []).map(tag => tag || '').filter(Boolean)
    });
    setCollectionTagInput('');
    setCurrentView('collection-edit');
  };

  const handleDeleteCollection = async (collection) => {
    if (!collection?._id) return;
    if (collection.promotedFrom) {
      addNotification?.({ message: "You cannot delete another vendor's collection.", type: 'info' });
      return;
    }
    const confirmDelete = window.confirm(`Delete collection "${collection.name}"? Fabrics will remain but their collection link will be cleared.`);
    if (!confirmDelete) return;

    try {
      await deleteFabricCollection(collection._id);
      await Promise.all([
        fetchFabricCollections(),
        loadPromotedCatalog(),
        fetchProducts(1, false)
      ]);
      if (showNotification) {
        showNotification('Fabric collection deleted.', 'success');
      } else {
        addNotification?.({ message: 'Fabric collection deleted.', type: 'success' });
      }
    } catch (error) {
      console.error('Failed to delete fabric collection:', error);
      const message = error?.response?.data?.message || 'Failed to delete fabric collection.';
      if (showNotification) {
        showNotification(message, 'error');
      } else {
        addNotification?.({ message, type: 'error' });
      }
    }
  };

  const handleToggleCollectionPublic = useCallback(
    async (collection, nextPublic) => {
      if (!collection?._id) return;
      if (collection.promotedFrom) {
        addNotification?.({ message: "You can't change visibility for another vendor's collection.", type: 'info' });
        return;
      }
      if (collectionPublicSavingId) return;

      setCollectionPublicSavingId(collection._id);
      try {
        await updateFabricCollection(collection._id, { isPublic: nextPublic });

        setFabricCollections((prev) =>
          (prev || []).map((c) => (String(c._id) === String(collection._id) ? { ...c, isPublic: nextPublic } : c))
        );

        addNotification?.({
          message: nextPublic ? 'Collection is now public.' : 'Collection is now private.',
          type: 'success',
        });
      } catch (error) {
        const message = error?.response?.data?.message || 'Failed to update collection visibility.';
        addNotification?.({ message, type: 'error' });
      } finally {
        setCollectionPublicSavingId(null);
        fetchFabricCollections({ skipCache: true });
      }
    },
    [addNotification, collectionPublicSavingId, fetchFabricCollections]
  );

  const handleTogglePromotedCollectionVisibility = useCallback(
    async (collection, nextPublic) => {
      if (!collection?._id) return;
      if (!collection.promotedFrom) {
        addNotification?.({ message: "This option is only for promoted collections.", type: 'info' });
        return;
      }
      if (promotedCollectionVisibilitySavingId) return;

      setPromotedCollectionVisibilitySavingId(collection._id);
      try {
        await updatePromotedCollectionVisibility(collection._id, nextPublic);

        setPromotedCollections((prev) =>
          (prev || []).map((c) =>
            String(c._id) === String(collection._id) ? { ...c, promoterVisibility: nextPublic } : c
          )
        );

        addNotification?.({
          message: nextPublic ? 'Promoted collection is now public.' : 'Promoted collection is now private.',
          type: 'success',
        });
      } catch (error) {
        const message = error?.response?.data?.message || 'Failed to update promoted collection visibility.';
        addNotification?.({ message, type: 'error' });
      } finally {
        setPromotedCollectionVisibilitySavingId(null);
        loadPromotedCatalog();
      }
    },
    [addNotification, promotedCollectionVisibilitySavingId, loadPromotedCatalog]
  );

  useEffect(() => {
    return () => {
      if (fabricCollectionForm.imagePreview && typeof window !== 'undefined' && fabricCollectionForm.imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(fabricCollectionForm.imagePreview);
      }
    };
  }, [fabricCollectionForm.imagePreview]);

  const handleToggleVisibility = useCallback(async (fabric, makePublic) => {
    if (fabric?.promotedFrom) {
      addNotification({ message: 'Visibility is managed by the fabric owner.', type: 'info' });
      return;
    }
    setVisibilityLoadingId(fabric._id);
    try {
      const updatedProduct = await updateProductVisibility(fabric._id, makePublic);
      setProducts((prev) =>
        prev.map((item) => (item._id === fabric._id ? { ...item, isPublic: updatedProduct.isPublic, publicAt: updatedProduct.publicAt } : item))
      );
      addNotification({
        message: updatedProduct.isPublic ? 'Fabric is now public' : 'Fabric is now private',
        type: 'success'
      });
    } catch (error) {
      console.error('Error updating visibility:', error);
      addNotification({ message: 'Failed to update fabric visibility', type: 'error' });
    } finally {
      setVisibilityLoadingId(null);
    }
  }, [addNotification]);

  // Handle fabric catalogue view
  const handleViewCatalogue = useCallback((fabric) => {
    setCatalogueFabric(fabric);
  }, []);

  // Handle virtual try-on with product info
  const handleVirtualTryOn = (product) => {
    try {
      console.log('handleVirtualTryOn called with product:', product);
      
      // Navigate to virtual try-on page
      console.log('Navigating to virtual try-on page');
      setActiveView('virtualtry');
      setPreSelectedProduct(product); // Set the product for virtual try-on
      
      // Show success notification
      if (showNotification) {
        showNotification(`Product "${product.name}" loaded for virtual try-on!`, 'success');
      } else if (addNotification) {
        addNotification({ 
          message: `Product "${product.name}" loaded for virtual try-on!`, 
          type: 'success' 
        });
      }
    } catch (error) {
      console.error('Error navigating to virtual try-on:', error);
      if (showNotification) {
        showNotification('Failed to load product for virtual try-on', 'error');
      } else if (addNotification) {
        addNotification({ 
          message: 'Failed to load product for virtual try-on', 
          type: 'error' 
        });
      }
    }
  };

  // Handle generate with model - navigate to generate kit page with product auto-populated
  const handleGenerateWithModel = useCallback((product) => {
    try {
      console.log('handleGenerateWithModel called with product:', product);
      
      // Prepare all variants for auto-selection
      const allVariants = [];
      
      // Add primary image as first variant
      const primaryImageUrl = product.images?.original?.s3Url || product.images?.original?.imgbbUrl || product.images?.original?.url || product.image;
      if (primaryImageUrl) {
        allVariants.push({
          image: { s3Url: primaryImageUrl },
          colorName: 'Primary',
          isPrimary: true
        });
      }
      
      // Add all color variants
      if (product.colorVariants && product.colorVariants.length > 0) {
        product.colorVariants.forEach((variant, index) => {
          allVariants.push({
            ...variant,
            isPrimary: false,
            originalIndex: index
          });
        });
      }
      
      // Auto-populate product details with variant information
      const productDetails = {
        name: product.name || '',
        description: product.description || '',
        image: primaryImageUrl || '',
        category: product.category || '',
        type: product.type || product.productType || '',
        targetAudience: product.targetAudience || 'General Audience',
        fabricAnalysis: product.fabricAnalysis || null,
        fabricWeight: product.fabricAnalysis?.fabric_analysis?.gsm_weight || product.fabricWeight || '',
        // Include full product data for variant selection
        selectedProduct: product,
        savedProductId: product._id, // Enable shade card/color updates to persist when user adds shade cards
        fabricShadeCards: product.shadeCards || (product.shadeCard ? [product.shadeCard] : []),
        colorVariants: product.colorVariants || [],
        selectedVariants: allVariants, // All variants auto-selected
        selectedVariantIndex: 0 // Default to primary
      };
      
      // Set the product details in the parent component
      if (setProductDetails) {
        setProductDetails(productDetails);
      }
      
      // Navigate to generate kit page (lite flow when opened from fabrics lite)
      console.log('Navigating to generate kit page with all variants selected');
      setActiveView(liteMode ? 'generate-lite' : 'generate');
      
      // Show success notification
      const variantCount = allVariants.length;
      if (showNotification) {
        showNotification(`Fabric "${product.name}" loaded with ${variantCount} color variant${variantCount > 1 ? 's' : ''} for kit generation!`, 'success');
      } else if (addNotification) {
        addNotification({ 
          message: `Fabric "${product.name}" loaded with ${variantCount} color variant${variantCount > 1 ? 's' : ''} for kit generation!`, 
          type: 'success' 
        });
      }
    } catch (error) {
      console.error('Error navigating to generate kit:', error);
      if (showNotification) {
        showNotification('Failed to load fabric for kit generation', 'error');
      } else if (addNotification) {
        addNotification({ 
          message: 'Failed to load fabric for kit generation', 
          type: 'error' 
        });
      }
    }
  }, [setProductDetails, setActiveView, showNotification, addNotification, liteMode]);

  // Custom Fabric Card Component - Memoized for performance
  // FabricCard moved outside component

  // Show add/edit form as full screen
  if (currentView === 'add' || currentView === 'edit') {
    return (
      <ProductAddForm 
        product={currentView === 'edit' ? editingProduct : null}
        onClose={currentView === 'edit' ? handleEditClose : handleAddClose}
        onProductAdded={handleProductAdded}
        onProductUpdated={handleProductUpdated}
      />
    );
  }

  if (currentView === 'collection-create' || currentView === 'collection-edit') {
    const isEditingCollectionView = currentView === 'collection-edit';

    return (
      <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm overflow-hidden">
        <div className="h-full w-full flex flex-col bg-white">
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-3 sm:px-6 pb-4 pt-[max(1rem,env(safe-area-inset-top))] flex items-center justify-between shadow-lg">
            <div className="flex min-w-0 items-center gap-2 sm:gap-3">
              <button
                onClick={handleCloseFabricCollectionPage}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full hover:bg-white/15 transition-colors active:scale-95"
                title="Go back"
                aria-label="Go back"
              >
                <ArrowLeft size={24} />
              </button>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold">
                  {isEditingCollectionView ? 'Edit Fabric Collection' : 'Create Fabric Collection'}
                </h1>
                <p className="text-sm text-purple-100">
                  Group related fabrics with shared properties for quicker access.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs sm:text-sm text-purple-100">
              {isEditingCollectionView
                ? `${editingCollection?.fabrics?.length || 0} fabrics linked`
                : `${fabricCollectionForm.selectedFabrics.length} selected`}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
              <section className="bg-white rounded-2xl shadow-lg border border-purple-100 p-6 space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Collection Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={fabricCollectionForm.name}
                    onChange={(e) => handleFabricCollectionNameChange(e.target.value)}
                    placeholder="e.g., Festive Silk Collection"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm sm:text-base"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Collection Image
                      <span className="text-xs text-gray-400 ml-2">(optional)</span>
                    </label>
                    {fabricCollectionForm.imagePreview && (
                      <button
                        type="button"
                        onClick={handleFabricCollectionImageClear}
                        className="text-xs font-semibold text-red-500 hover:text-red-600"
                      >
                        Remove image
                      </button>
                    )}
                  </div>
                  <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-purple-500 transition-colors bg-purple-50/30">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFabricCollectionImageSelect}
                      className="hidden"
                      id="fabric-collection-image-upload"
                    />
                    <label
                      htmlFor="fabric-collection-image-upload"
                      className="flex flex-col items-center justify-center cursor-pointer space-y-3"
                    >
                      {fabricCollectionForm.imagePreview ? (
                        <>
                          <img
                            src={fabricCollectionForm.imagePreview}
                            alt="Collection preview"
                            className="w-full h-48 object-cover rounded-xl shadow"
                          />
                          <p className="text-sm text-gray-600">
                            Click to {isEditingCollectionView ? 'replace' : 'change'} image
                          </p>
                        </>
                      ) : (
                        <>
                          <div className="w-14 h-14 bg-purple-100 rounded-full flex items-center justify-center">
                            <ImageIcon size={28} className="text-purple-600" />
                          </div>
                          <p className="text-sm text-gray-600">Click to upload image</p>
                          <p className="text-xs text-gray-400">PNG, JPG, GIF up to 10MB</p>
                        </>
                      )}
                    </label>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Collection Properties
                    </label>
                    <button
                      type="button"
                      onClick={handleAddFabricCollectionProperty}
                      className="inline-flex items-center gap-2 text-sm font-medium text-purple-600 hover:text-purple-700"
                    >
                      <Plus size={16} />
                      Add property
                    </button>
                  </div>

                  <div className="space-y-3">
                    {fabricCollectionForm.properties.map((property, index) => (
                      <div
                        key={`fabric-collection-property-${index}`}
                        className="flex flex-col sm:flex-row gap-3 sm:items-center bg-purple-50/60 border border-purple-100 rounded-xl p-3"
                      >
                        <input
                          type="text"
                          value={property.key}
                          onChange={(e) => handleFabricCollectionPropertyChange(index, 'key', e.target.value)}
                          placeholder="Property key (e.g., Pattern)"
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                        />
                        <input
                          type="text"
                          value={property.value}
                          onChange={(e) => handleFabricCollectionPropertyChange(index, 'value', e.target.value)}
                          placeholder="Property value (e.g., Floral)"
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveFabricCollectionProperty(index)}
                          className="inline-flex items-center justify-center px-3 py-2 text-sm text-red-500 hover:text-red-600"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Collection Tags
                      <span className="ml-1 text-xs text-gray-400">(press Enter to add)</span>
                    </label>
                    <span className="text-xs text-gray-500">
                      {fabricCollectionForm.tags.length}/30
                    </span>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <input
                      type="text"
                      value={collectionTagInput}
                      onChange={(e) => setCollectionTagInput(e.target.value)}
                      onKeyDown={handleFabricCollectionTagKeyDown}
                      placeholder="Add tags (e.g., festive, silk, premium)"
                      className="w-full sm:flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                    />
                    <button
                      type="button"
                      onClick={handleFabricCollectionTagAdd}
                      className="w-full sm:w-auto px-4 py-2.5 bg-purple-500 text-white rounded-lg font-semibold text-sm hover:bg-purple-600 transition-colors"
                    >
                      Add Tag
                    </button>
                  </div>
                  {fabricCollectionForm.tags.length > 0 ? (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {fabricCollectionForm.tags.map(tag => (
                        <span
                          key={tag}
                          className="inline-flex items-center gap-1 bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-xs font-medium"
                        >
                          #{tag}
                          <button
                            type="button"
                            onClick={() => handleFabricCollectionTagRemove(tag)}
                            className="text-purple-500 hover:text-purple-700"
                          >
                            <X size={12} />
                          </button>
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="mt-2 text-xs text-gray-400">
                      Tags help you search and organise your collections quickly.
                    </p>
                  )}
                </div>
              </section>

              <section className="bg-white rounded-2xl shadow-lg border border-purple-100 p-6 space-y-3">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-gray-700">
                    Upload Fabric Images
                  </h3>
                  <span className="text-xs text-gray-500">
                    Each image will be saved as a fabric material
                  </span>
                </div>
                <div className="border-2 border-dashed border-purple-300 rounded-xl p-6 text-center hover:border-purple-500 transition-colors bg-purple-50/30">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => {
                      if (e.target.files && e.target.files.length > 0) {
                        handleFabricImagesUpload(e.target.files);
                      }
                      // Reset input
                      e.target.value = '';
                    }}
                    className="hidden"
                    id="fabric-images-upload"
                    disabled={uploadingFabricImages.length > 0}
                  />
                  <label
                    htmlFor="fabric-images-upload"
                    className={`flex flex-col items-center justify-center cursor-pointer space-y-3 ${
                      uploadingFabricImages.length > 0 ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    <div className="w-14 h-14 bg-purple-100 rounded-full flex items-center justify-center">
                      <Upload size={28} className="text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">
                        {uploadingFabricImages.length > 0
                          ? `Uploading ${uploadingFabricImages.length} image(s)...`
                          : 'Click to upload fabric images'}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        PNG, JPG, HEIC, WEBP up to 10MB each (multiple files supported)
                      </p>
                    </div>
                  </label>
                </div>
                {uploadingFabricImages.length > 0 && (
                  <div className="space-y-2 mt-4">
                    {uploadingFabricImages.map((file, index) => {
                      // Find progress by matching index with fileIds
                      const fileIds = Object.keys(uploadingFabricsProgress).sort();
                      const fileId = fileIds[index] || `pending-${index}`;
                      const progress = uploadingFabricsProgress[fileId] || { progress: 0, status: 'pending' };
                      const preview = uploadingFabricPreviewUrls[index] || '';
                      return (
                        <div
                          key={`${file.name}-${index}`}
                          className="flex items-center gap-3 p-3 bg-purple-50 border border-purple-200 rounded-lg"
                        >
                          <div className="relative w-16 h-16 flex-shrink-0">
                            <img
                              src={preview}
                              alt={file.name}
                              className="w-full h-full object-cover rounded"
                            />
                            {progress.status === 'completed' && (
                              <div className="absolute inset-0 bg-green-500/20 flex items-center justify-center rounded">
                                <Loader2 size={16} className="text-green-600" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-gray-700 truncate">{file.name}</p>
                            <div className="mt-1">
                              <div className="w-full bg-gray-200 rounded-full h-1.5">
                                <div
                                  className={`h-1.5 rounded-full transition-all ${
                                    progress.status === 'error'
                                      ? 'bg-red-500'
                                      : progress.status === 'completed'
                                      ? 'bg-green-500'
                                      : 'bg-purple-500'
                                  }`}
                                  style={{ width: `${progress.progress}%` }}
                                />
                              </div>
                              <p className="text-[10px] text-gray-500 mt-0.5">
                                {progress.status === 'compressing' && 'Compressing...'}
                                {progress.status === 'uploading' && `Uploading... ${progress.progress}%`}
                                {progress.status === 'analyzing' && 'Analyzing fabric...'}
                                {progress.status === 'creating' && 'Creating fabric material...'}
                                {progress.status === 'completed' && 'Completed'}
                                {progress.status === 'error' && `Error: ${progress.error || 'Failed'}`}
                                {progress.status === 'pending' && 'Pending...'}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>

              {/* Show uploaded fabrics as thumbnails */}
              {uploadedFabrics.length > 0 && (
                <section className="bg-white rounded-2xl shadow-lg border border-purple-100 p-6 space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium text-gray-700">
                      Uploaded Fabrics ({uploadedFabrics.length})
                    </h3>
                    <span className="text-xs text-green-600 font-medium">
                      ✓ Added to collection
                    </span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {uploadedFabrics.map(fabric => {
                      const image =
                        fabric.image ||
                        fabric.images?.original?.s3Url ||
                        fabric.images?.original?.imgbbUrl ||
                        fabric.images?.original?.url ||
                        'https://via.placeholder.com/200x200?text=No+Image';
                      const thumb = image ? toThumbnailUrl(image) : image;
                      const isSelected = fabricCollectionForm.selectedFabrics.includes(fabric._id);
                      return (
                        <div
                          key={fabric._id}
                          className={`group border rounded-lg overflow-hidden text-left transition-all ${
                            isSelected ? 'border-green-500 ring-2 ring-green-200' : 'border-gray-200'
                          }`}
                        >
                          <div className="relative aspect-square bg-gray-100 overflow-hidden">
                            <LazyImage
                              src={thumb}
                              fallbackSrc={image}
                              alt={fabric.name}
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-green-600/30 flex items-center justify-center">
                              <span className="bg-white text-green-600 text-xs font-semibold px-2 py-1 rounded-full">
                                Uploaded
                              </span>
                            </div>
                          </div>
                          <div className="p-2">
                            <h4 className="text-xs font-semibold text-gray-800 line-clamp-1">{fabric.name}</h4>
                            <p className="text-[11px] text-gray-500 line-clamp-2">
                              {fabric.description || 'No description'}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              )}

              <section className="bg-white rounded-2xl shadow-lg border border-purple-100 p-6 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-medium text-gray-700">
                    Add Fabrics from Existing Materials
                  </h3>
                  <span className="text-xs text-gray-500">
                    {isEditingCollectionView
                      ? 'Update collection with new selection'
                      : `${fabricCollectionForm.selectedFabrics.length} selected`}
                  </span>
                </div>
                {availableModalFabrics.length === 0 ? (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-500">
                    {hasNextPage ? (
                      <div className="flex flex-col items-center gap-2 text-purple-600">
                        <Loader2 size={18} className="animate-spin" />
                        <span className="text-sm font-medium">Searching for available fabrics...</span>
                        <div ref={collectionObserverRef} className="h-6" />
                      </div>
                    ) : (
                      'All fabrics are already part of collections. Add new fabrics to include them here.'
                    )}
                  </div>
                ) : (
                  <div className="max-h-80 overflow-y-auto pr-1 custom-scrollbar">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {availableModalFabrics.map(fabric => {
                        const image =
                          fabric.image ||
                          fabric.images?.original?.s3Url ||
                          fabric.images?.original?.imgbbUrl ||
                          fabric.images?.original?.url ||
                          'https://via.placeholder.com/200x200?text=No+Image';
                        const thumb = image ? toThumbnailUrl(image) : image;
                        const isSelected = fabricCollectionForm.selectedFabrics.includes(fabric._id);
                        return (
                          <button
                            type="button"
                            key={fabric._id}
                            onClick={() => handleToggleFabricInCollection(fabric._id)}
                            className={`group border rounded-lg overflow-hidden text-left transition-all ${
                              isSelected ? 'border-purple-500 ring-2 ring-purple-200' : 'border-gray-200 hover:border-purple-300'
                            }`}
                          >
                            <div className="relative aspect-square bg-gray-100 overflow-hidden">
                              <LazyImage
                                src={thumb}
                                fallbackSrc={image}
                                alt={fabric.name}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                              />
                              {isSelected && (
                                <div className="absolute inset-0 bg-purple-600/30 flex items-center justify-center">
                                  <span className="bg-white text-purple-600 text-xs font-semibold px-2 py-1 rounded-full">
                                    Selected
                                  </span>
                                </div>
                              )}
                            </div>
                            <div className="p-2">
                              <h4 className="text-xs font-semibold text-gray-800 line-clamp-1">{fabric.name}</h4>
                              <p className="text-[11px] text-gray-500 line-clamp-2">
                                {fabric.description || 'No description'}
                              </p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    {hasNextPage && (
                      <div ref={collectionObserverRef} className="flex justify-center py-3">
                        {isLoadingMore ? (
                          <div className="flex items-center gap-2 text-purple-600 text-xs font-medium">
                            <Loader2 size={16} className="animate-spin" />
                            <span>Loading more fabrics...</span>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">Scroll to load more fabrics</span>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </section>
            </div>
          </div>

          <div className="px-4 sm:px-6 py-4 bg-white border-t border-gray-200 flex flex-col sm:flex-row sm:justify-end gap-3">
            <button
              type="button"
              onClick={handleCloseFabricCollectionPage}
              className="px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSaveFabricCollection}
              disabled={isSavingFabricCollection}
              className="px-4 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSavingFabricCollection ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  <span>{isEditingCollectionView ? 'Updating...' : 'Saving...'}</span>
                </>
              ) : (
                <span>{isEditingCollectionView ? 'Update Collection' : 'Save Collection'}</span>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-3 sm:py-6">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
        <div className="mb-4 sm:mb-6">
          <div className="bg-white/80 backdrop-blur rounded-xl p-1 shadow-sm border border-purple-100 flex w-full sm:w-auto gap-1">
            {liteMode ? (
              <>
                <button
                  type="button"
                  onClick={() => handleActiveProductsTabChange('collections')}
                  className={`flex-1 sm:flex-none px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                    activeProductsTab === 'collections'
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  Collections
                </button>
                <button
                  type="button"
                  onClick={() => handleActiveProductsTabChange('fabrics')}
                  className={`flex-1 sm:flex-none px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                    activeProductsTab === 'fabrics'
                      ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  Fabrics
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => handleActiveProductsTabChange('fabrics')}
                  className={`flex-1 sm:flex-none px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                    activeProductsTab === 'fabrics'
                      ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  Fabrics
                </button>
                <button
                  type="button"
                  onClick={() => handleActiveProductsTabChange('collections')}
                  className={`flex-1 sm:flex-none px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                    activeProductsTab === 'collections'
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  Collections
                </button>
              </>
            )}
          </div>

          {activeProductsTab === 'fabrics' ? (
            <div className="mt-4 space-y-3">
              {liteMode ? (
                <div className="w-full relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search size={18} className="text-gray-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search by fabric type, color, season, garment type, or any attribute..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white shadow-sm hover:shadow-md transition-shadow duration-200"
                  />
                  {searchInput && (
                    <button
                      onClick={() => setSearchInput('')}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center hover:bg-gray-100 rounded-r-xl transition-colors duration-200"
                      aria-label="Clear search"
                    >
                      <X size={16} className="text-gray-400 hover:text-gray-600" />
                    </button>
                  )}
                </div>
              ) : (
              <>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 sm:items-center sm:justify-between">
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setCurrentView('add')}
                    className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 sm:px-6 py-3 rounded-xl flex items-center justify-center sm:justify-start shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-base active:scale-[0.99]"
                  >
                    <Plus size={18} className="mr-2" />
                    Add Fabric
                  </button>
                  {followersCount !== null && (
                    <div className="flex items-center gap-2 px-4 py-2 bg-white border border-purple-100 rounded-xl shadow-sm">
                      <Users size={18} className="text-purple-500" />
                      <span className="text-sm font-semibold text-gray-700">
                        {followersCount} follower{followersCount !== 1 ? 's' : ''}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setShowFilters(v => !v)}
                      className="w-full sm:w-auto px-4 py-3 rounded-xl bg-white border border-gray-200 text-gray-700 shadow-sm hover:shadow-md transition-all duration-200 font-semibold text-sm flex items-center justify-center"
                    >
                      Filters{appliedFilterCount ? ` (${appliedFilterCount})` : ''}
                    </button>
                    {appliedFilterCount > 0 && (
                      <button
                        type="button"
                        onClick={clearFilters}
                        className="px-3 py-3 rounded-xl bg-gray-50 border border-gray-200 text-gray-600 hover:bg-gray-100 text-sm font-semibold"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>
                <div className="w-full sm:w-auto sm:max-w-md lg:max-w-lg relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search size={18} className="text-gray-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search by fabric type, color, season, garment type, or any attribute..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    disabled={Boolean(visualSearchImageUrl)}
                    className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white shadow-sm hover:shadow-md transition-shadow duration-200"
                  />
                  {searchInput && (
                    <button
                      onClick={() => setSearchInput('')}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center hover:bg-gray-100 rounded-r-xl transition-colors duration-200"
                      aria-label="Clear search"
                    >
                      <X size={16} className="text-gray-400 hover:text-gray-600" />
                    </button>
                  )}
                </div>
                <input
                  ref={visualSearchInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleVisualSearchImageSelect}
                />
                <input
                  ref={visualSearchCameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={handleVisualSearchCameraCapture}
                />
                <div ref={imageSearchMenuRef} className="relative w-full sm:w-auto">
                  <button
                    type="button"
                    onClick={() => setShowImageSearchOptions((prev) => !prev)}
                    disabled={isVisualSearchUploading || isCameraStarting}
                    className={`w-full sm:w-auto px-4 py-3 rounded-xl border text-sm font-semibold transition-all duration-200 flex items-center justify-center gap-2 ${
                      (isVisualSearchUploading || isCameraStarting)
                        ? 'bg-gray-100 border-gray-200 text-gray-500 cursor-not-allowed'
                        : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                    }`}
                    title="Search by fabric image"
                  >
                    {(isVisualSearchUploading || isCameraStarting) ? <Loader2 size={16} className="animate-spin" /> : <ScanSearch size={16} />}
                    <span>{isVisualSearchUploading ? 'Uploading...' : isCameraStarting ? 'Opening camera...' : 'Image Search'}</span>
                  </button>
                  {showImageSearchOptions && !isVisualSearchUploading && (
                    <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-xl shadow-lg z-30 overflow-hidden">
                      <button
                        type="button"
                        onClick={() => openImageSearchOption('camera')}
                        className="w-full px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                      >
                        <Camera size={16} className="text-blue-600" />
                        <span>Take Photo</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => openImageSearchOption('upload')}
                        className="w-full px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 border-t border-gray-100 flex items-center gap-2"
                      >
                        <Upload size={16} className="text-indigo-600" />
                        <span>Upload from Device</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
              {visualSearchImageUrl && (
                <div className="flex items-center gap-3 px-1">
                  <div className="w-10 h-10 rounded-lg overflow-hidden border border-gray-200 bg-white">
                    {visualSearchImagePreview ? (
                      <img src={visualSearchImagePreview} alt="Visual search" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ImageIcon size={14} className="text-gray-400" />
                      </div>
                    )}
                  </div>
                  <span className="text-xs text-gray-600 font-medium">Searching by image similarity</span>
                  <button
                    type="button"
                    onClick={clearVisualSearch}
                    className="px-2 py-1 text-xs rounded-md border border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
                  >
                    Clear image
                  </button>
                </div>
              )}
              <div className="text-xs text-gray-500 px-1 flex flex-wrap items-center gap-2">
                <span
                  className={`px-2 py-0.5 rounded-full font-semibold border ${
                    visualSearchImageUrl
                      ? 'bg-blue-50 text-blue-700 border-blue-100'
                      : 'bg-emerald-50 text-emerald-700 border-emerald-100'
                  }`}
                >
                  {visualSearchImageUrl
                    ? 'Visual image search enabled'
                    : 'Semantic search enabled'}
                </span>
                <span className="font-medium">Search by:</span>
                <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full">fabric type</span>
                <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full">color</span>
                <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full">season</span>
                <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full">garment</span>
                <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full">name</span>
              </div>

              {showFilters && (
                <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Fabric type</label>
                      <select
                        value={filterDraft.fabricType}
                        onChange={(e) => setFilterDraft(prev => ({ ...prev, fabricType: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                      >
                        <option value="">Any fabric type</option>
                        {fabricTypes.map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Weave / Knit</label>
                      <select
                        value={filterDraft.weaveOrKnit}
                        onChange={(e) => setFilterDraft(prev => ({ ...prev, weaveOrKnit: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                      >
                        <option value="">Any weave/knit</option>
                        {weaveKnitValues.map((value) => (
                          <option key={value} value={value}>{value}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Opacity</label>
                      <select
                        value={filterDraft.opacity}
                        onChange={(e) => setFilterDraft(prev => ({ ...prev, opacity: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                      >
                        <option value="">Any opacity</option>
                        {opacityValues.map((value) => (
                          <option key={value} value={value}>{value}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Stretch</label>
                      <select
                        value={filterDraft.stretch}
                        onChange={(e) => setFilterDraft(prev => ({ ...prev, stretch: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                      >
                        <option value="">Any stretch</option>
                        {stretchValues.map((value) => (
                          <option key={value} value={value}>{value}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Color</label>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-6 h-6 rounded border border-gray-300 flex-shrink-0"
                          style={{ backgroundColor: getColorHex(filterDraft.color) || 'transparent' }}
                          title={filterDraft.color ? getColorLabel(filterDraft.color) : 'Any color'}
                        />
                        <select
                          value={filterDraft.color}
                          onChange={(e) => setFilterDraft(prev => ({ ...prev, color: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                        >
                          <option value="">Any color</option>
                          {colorValues.map((value) => (
                            <option key={value} value={value}>
                              {getColorLabel(value)}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-end gap-2 mt-4">
                    <button
                      type="button"
                      onClick={() => setShowFilters(false)}
                      className="px-4 py-2 rounded-lg border border-gray-200 bg-white text-gray-700 text-sm font-semibold"
                    >
                      Close
                    </button>
                    <button
                      type="button"
                      onClick={applyFilters}
                      className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold"
                    >
                      Apply
                    </button>
                  </div>
                </div>
              )}
              </>
              )}
            </div>
          ) : (
          <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
            <div className="w-full flex flex-col sm:flex-row gap-3 sm:gap-4">
              {!liteMode && (
              <button
                type="button"
                onClick={handleOpenFabricCollectionPage}
                className="w-full sm:w-auto bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 sm:px-6 py-3 rounded-xl flex items-center justify-center sm:justify-start shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-base active:scale-[0.99]"
              >
                <Layers size={18} className="mr-2" />
                Add Fabric Collection
              </button>
              )}
              <div className={`relative ${liteMode ? 'w-full' : 'w-full sm:w-auto sm:max-w-xs'}`}>
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search collections or fabrics..."
                  value={collectionSearchInput}
                  onChange={(e) => setCollectionSearchInput(e.target.value)}
                  className={`w-full pl-9 pr-4 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm ${
                    liteMode ? 'py-3 rounded-xl' : 'py-2 rounded-lg'
                  }`}
                />
              </div>
            </div>
            {!liteMode && !collectionsLoading && !promotedCatalogLoading && (
              <span className="text-sm text-gray-500 sm:text-right">
                {displayCollections.length} collection{displayCollections.length === 1 ? '' : 's'}
              </span>
            )}
            {!liteMode && promotedCatalogLoading && (
              <span className="text-xs text-amber-700 sm:text-right">Loading promoted vendors…</span>
            )}
          </div>
        )}
        </div>

        {activeProductsTab === 'collections' ? (
          <section className="mb-6">
            {collectionsLoading || (promotedCatalogLoading && displayCollections.length === 0) ? (
              <div className="flex items-center justify-center py-12 text-purple-600">
                <Loader2 size={22} className="animate-spin mr-2" />
                <span className="font-medium text-sm sm:text-base">Loading collections...</span>
              </div>
            ) : displayCollections.length > 0 ? (
              liteMode ? (
                <div className="space-y-5" style={{ WebkitOverflowScrolling: 'touch' }}>
                  {displayCollections.map((collection) => (
                    <FabricCollectionLiteRow
                      key={collection.promotedFrom ? `promoted-${collection._id}` : collection._id}
                      collection={collection}
                      childCollections={childCollectionsByParentId.get(String(collection._id)) || []}
                      fabrics={resolveCollectionFabrics(collection)}
                      onOpenCollection={openFabricCollectionDetail}
                      onOpenFabric={handleViewCatalogue}
                    />
                  ))}
                </div>
              ) : (
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
                {displayCollections.map(collection => (
                  <FabricCollectionCard
                    key={collection.promotedFrom ? `promoted-${collection._id}` : collection._id}
                    collection={collection}
                    onOpen={openFabricCollectionDetail}
                    onDelete={liteMode ? undefined : handleDeleteCollection}
                    onEdit={liteMode ? undefined : handleEditCollection}
                    onTogglePublic={liteMode ? undefined : handleToggleCollectionPublic}
                    isPublicSaving={collectionPublicSavingId === collection._id}
                    onTogglePromoterVisibility={liteMode ? undefined : handleTogglePromotedCollectionVisibility}
                    isPromoterVisibilitySaving={promotedCollectionVisibilitySavingId === collection._id}
                    liteMode={liteMode}
                  />
                ))}
              </div>
              )
            ) : (
              <div className="bg-white rounded-xl sm:rounded-2xl p-6 sm:p-12 border border-purple-100 shadow-sm text-center">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-r from-purple-100 to-pink-100 rounded-lg sm:rounded-xl flex items-center justify-center mx-auto mb-4 sm:mb-6">
                  <Layers size={24} className="text-purple-500 sm:w-8 sm:h-8" />
                </div>
                <h3 className="text-lg sm:text-2xl font-bold text-gray-800 mb-2 sm:mb-3">No collections yet</h3>
                <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-8 max-w-md mx-auto">
                  {liteMode
                    ? 'No fabric collections in your library yet.'
                    : 'Organise your fabrics into collections for faster workflows.'}
                </p>
                {!liteMode && (
                <button
                  onClick={handleOpenFabricCollectionPage}
                  className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 sm:px-8 py-2.5 sm:py-3 rounded-lg sm:rounded-xl font-semibold hover:from-purple-600 hover:to-pink-600 transition-all duration-300 transform hover:scale-105 shadow-lg flex items-center mx-auto text-sm sm:text-base"
                >
                  <Plus size={16} className="mr-2 sm:w-4.5 sm:h-4.5" />
                  Create Your First Collection
                </button>
                )}
              </div>
            )}
          </section>
        ) : isLoading ? (
          visualSearchImageUrl ? (
            <div className="space-y-4 sm:space-y-6">
              <div className="relative overflow-hidden rounded-2xl border border-cyan-200 bg-gradient-to-r from-slate-900 via-blue-900 to-slate-900 p-5 sm:p-6 text-white shadow-lg">
                <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_20%_20%,#22d3ee_0%,transparent_35%),radial-gradient(circle_at_80%_80%,#6366f1_0%,transparent_35%)]" />
                <div className="absolute inset-y-0 -left-1/3 w-1/2 bg-white/20 blur-2xl animate-[pulse_2.4s_ease-in-out_infinite]" />
                <div className="relative flex items-start sm:items-center justify-between gap-4">
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-cyan-200 font-semibold">Visual Search</p>
                    <h3 className="text-base sm:text-lg font-semibold mt-1">Scanning textile patterns and texture signatures</h3>
                    <p className="text-xs sm:text-sm text-blue-100/90 mt-1">Matching your input image against fabric vectors in real time.</p>
                  </div>
                  <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center shrink-0">
                    <Loader2 size={20} className="animate-spin text-cyan-200" />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
                {Array.from({ length: 8 }).map((_, idx) => (
                  <div key={`visual-search-skeleton-${idx}`} className="rounded-xl border border-blue-100 bg-white/80 overflow-hidden shadow-sm">
                    <div className="aspect-square bg-gradient-to-br from-slate-200 via-blue-100 to-indigo-200 animate-pulse" />
                    <div className="p-3 space-y-2">
                      <div className="h-3 rounded bg-slate-200 animate-pulse" />
                      <div className="h-3 rounded bg-slate-100 w-2/3 animate-pulse" />
                      <div className="h-8 rounded-lg bg-gradient-to-r from-cyan-100 to-blue-100 animate-pulse mt-3" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center py-12 sm:py-20">
              <div className="text-center">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg sm:rounded-xl flex items-center justify-center mx-auto mb-3 sm:mb-4 animate-pulse">
                  <Layers size={20} className="text-white sm:w-6 sm:h-6" />
                </div>
                <p className="text-sm sm:text-base text-blue-600 font-medium">Loading your fabrics...</p>
              </div>
            </div>
          )
        ) : products.length === 0 && displayPromotedFabrics.length === 0 && promotedCatalogLoading ? (
          <div className="flex flex-col items-center justify-center py-16 sm:py-20 text-amber-800 gap-2">
            <Loader2 size={28} className="animate-spin text-amber-600" />
            <p className="text-sm sm:text-base font-medium text-center px-4">Loading public fabrics from vendors who approved your promotion…</p>
          </div>
        ) : products.length === 0 && displayPromotedFabrics.length === 0 ? (
          <div className="bg-white rounded-xl sm:rounded-2xl p-6 sm:p-12 border border-blue-100 shadow-sm text-center">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-lg sm:rounded-xl flex items-center justify-center mx-auto mb-4 sm:mb-6">
              <Layers size={24} className="text-blue-500 sm:w-8 sm:h-8" />
            </div>
            <h3 className="text-lg sm:text-2xl font-bold text-gray-800 mb-2 sm:mb-3">No fabrics yet</h3>
            <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-8 max-w-md mx-auto">
              {liteMode
                ? 'No fabrics in your library yet.'
                : 'Start building your fabric library to create amazing marketing kits'}
            </p>
            {!liteMode && (
            <button
              onClick={() => setCurrentView('add')}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 sm:px-8 py-2.5 sm:py-3 rounded-lg sm:rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 transform hover:scale-105 shadow-lg flex items-center mx-auto text-sm sm:text-base"
            >
              <Plus size={16} className="mr-2 sm:w-4.5 sm:h-4.5" />
              Add Your First Fabric
            </button>
            )}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
              {displayAllFabrics.map((fabric) => (
                <FabricCard
                  key={fabric.promotedFrom ? `promoted-${fabric._id}` : fabric._id}
                  fabric={fabric}
                  promotedFrom={fabric.promotedFrom}
                  isAdmin={isAdmin}
                  isVisibilityLoading={visibilityLoadingId === fabric._id}
                  isAnalysisLoading={analysisLoadingId === fabric._id}
                  onViewCatalogue={handleViewCatalogue}
                  onGenerate={handleGenerateWithModel}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onToggleVisibility={handleToggleVisibility}
                  onRunAnalysis={handleRunFabricAnalysis}
                  liteMode={liteMode}
                />
              ))}
            </div>

            {/* Infinite Scroll Trigger */}
            {hasNextPage && (
              <div ref={observerTarget} className="flex justify-center items-center py-8">
                {isLoadingMore ? (
                  <div className="flex items-center gap-2 text-blue-600">
                    <Loader2 size={20} className="animate-spin" />
                    <span className="text-sm font-medium">Loading more fabrics...</span>
                  </div>
                ) : (
                  <div className="h-20" /> // Spacer for intersection observer
                )}
              </div>
            )}
          </>
        )}
      </div>

      {isCameraCaptureOpen && (
        <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-2xl bg-white rounded-2xl overflow-hidden shadow-2xl">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-800">Capture Fabric Photo</h3>
              <button
                type="button"
                onClick={closeCameraCapture}
                className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                aria-label="Close camera"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-4 bg-gray-50">
              <div className="relative rounded-xl overflow-hidden bg-black aspect-video">
                <video
                  ref={cameraVideoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                />
              </div>
              <canvas ref={cameraCanvasRef} className="hidden" />
            </div>
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={closeCameraCapture}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={captureCameraPhoto}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 text-sm font-medium flex items-center gap-2"
              >
                <Camera size={16} />
                Capture
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fabric Catalogue View */}
      {catalogueFabric && (
        <FabricCatalogueView 
          fabric={catalogueFabric}
          liteMode={liteMode}
          onClose={() => setCatalogueFabric(null)}
          showNotification={showNotification || ((msg, type) => addNotification({ message: msg, type }))}
          onFabricUpdated={handleFabricUpdated}
          onGenerateFabric={handleGenerateWithModel}
          openAiStudio={openAiStudio}
        />
      )}

    </div>
  );
};

export default function MyProductsViewWithProvider(props) {
  return (
    <NotificationProvider>
      <MyProductsView {...props} />
    </NotificationProvider>
  );
} 